import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation, useParams, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api, ApiError } from '../lib/api';
import SitePlaybook from './SitePlaybook';
import RiskAssessmentList from './RiskAssessmentList';
import RosterCalendar from './RosterCalendar';
import { PortalSettingsModal } from './Portal';
import { LOG_TYPES, LOG_TYPE_CONFIG, formatDateTime, getRelativeTime } from '../lib/constants';
import {
  HomeIcon, ClipboardDocumentListIcon, MapPinIcon, ClockIcon,
  UserGroupIcon, Cog6ToothIcon, PlusIcon, ArrowRightOnRectangleIcon,
  BuildingOfficeIcon, ChartBarIcon, DocumentTextIcon, BellAlertIcon,
  UsersIcon, EyeIcon, FunnelIcon, ArrowDownTrayIcon
} from '@heroicons/react/24/outline';

function ManagerDashboard({ user }) {
  const [data, setData] = useState({ shifts:[], incidents:[], recentLogs:[], pendingTasks:0, totalOfficers:0, logsToday:0 });
  const [loading, setLoading] = useState(true);
  const [clientTasks, setClientTasks] = useState([]);
  const [ctModal, setCtModal] = useState(null);
  const [ctNotes, setCtNotes] = useState('');
  const [ctSubmitting, setCtSubmitting] = useState(false);
  const [ctFilter, setCtFilter] = useState('open');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshPulse, setRefreshPulse] = useState(false);
  const [forceEndId, setForceEndId] = useState(null);
  const [incidentAlerts, setIncidentAlerts] = useState([]);
  const [dashPanel, setDashPanel] = useState(null);
  const [reviewLog, setReviewLog] = useState(null);
  const [tasksDueList, setTasksDueList] = useState([]);
  const [tasksDoneList, setTasksDoneList] = useState([]);
  const seenIncidentIds = React.useRef(new Set());

  async function load() {
    try {
      const todayStart = new Date(); todayStart.setHours(0,0,0,0);
      const [shiftsRes, recentLogsRes, todayLogsRes, tasksRes, usersRes] = await Promise.all([
        api.shifts.list({ status: 'ACTIVE', limit: 50 }),
        api.logs.list({ limit: 8 }),
        api.logs.list({ limit: 500, from: todayStart.toISOString() }),
        api.tasks.list({ status: 'PENDING' }),
        api.users.list({ role: 'OFFICER' }),
      ]);
      // Fetch client tasks
      try { const ctRes = await api.alerts.list(); setClientTasks(ctRes.data || []); } catch {}
      const todayLogs = todayLogsRes.data || [];
      const incidents = todayLogs.filter(l => ['INCIDENT','ALARM','FIRE_ALARM','EMERGENCY'].includes(l.log_type) && l.review_status !== 'RESOLVED');
      const doneList = todayLogs.filter(l => l.type_data?.scheduled_task_id);
      const tasksDone = doneList.length;
      setTasksDueList(tasksRes.data || []);
      setTasksDoneList(doneList);
      setData({
        shifts: shiftsRes.data || [],
        incidents,
        recentLogs: recentLogsRes.data || [],
        pendingTasks: tasksRes.data?.length || 0,
        totalOfficers: usersRes.data?.length || 0,
        logsToday: todayLogs.length,
        tasksDone,
      });
      setLastRefresh(new Date());
      setRefreshPulse(true);
      setTimeout(() => setRefreshPulse(false), 1500);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  // Incident polling — every 10 seconds
  async function pollIncidents() {
    try {
      const tenMinsAgo = new Date(Date.now() - 600000).toISOString();
      const res = await api.logs.list({ from: tenMinsAgo, limit: 50 });
      const newIncidents = (res.data || []).filter(l =>
        ['INCIDENT','ALARM','FIRE_ALARM','EMERGENCY'].includes(l.log_type) && !seenIncidentIds.current.has(l.id)
      );
      newIncidents.forEach(l => seenIncidentIds.current.add(l.id));
      if (newIncidents.length > 0) {
        const newAlerts = newIncidents.map(l => ({
          id: l.id,
          msg: `${l.site?.name || 'Unknown site'} — ${new Date(l.occurred_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`,
          title: l.title || l.log_type,
          ts: Date.now(),
        }));
        setIncidentAlerts(prev => [...newAlerts, ...prev]);
        // Auto-dismiss after 30 seconds
        setTimeout(() => {
          const cutoff = Date.now() - 30000;
          setIncidentAlerts(prev => prev.filter(a => a.ts > cutoff || !newAlerts.find(n => n.id === a.id)));
        }, 30000);
      }
    } catch {}
  }

  // Seed seen IDs from initial load
  React.useEffect(() => {
    if (data.incidents.length > 0) {
      data.incidents.forEach(l => seenIncidentIds.current.add(l.id));
    }
  }, [data.incidents]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const ip = setInterval(pollIncidents, 10000);
    return () => { clearInterval(t); clearInterval(ip); };
  }, []);

  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',padding:'3rem'}}><div className="spinner" /></div>;

  // Compliance alerts — things wrong right now
  const alerts = [];
  if (data.incidents.length > 0) {
    const unacked = data.incidents.filter(i => !i.client_reportable);
    if (unacked.length) alerts.push({ level:'info', msg:`${unacked.length} incident${unacked.length>1?'s':''} recorded tonight — review required`, site: null });
  }

  const shiftDuration = (s) => {
    const mins = Math.floor((Date.now() - new Date(s.checked_in_at||s.start_time).getTime()) / 60000);
    return mins < 60 ? `${mins}m` : `${Math.floor(mins/60)}h ${mins%60}m`;
  };

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Operations Command</div>
        <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
          {lastRefresh && (
            <span style={{display:'inline-flex',alignItems:'center',gap:'0.375rem',fontSize:'0.75rem',color:'var(--text-3)'}}>
              <span style={{width:6,height:6,borderRadius:'50%',background:refreshPulse?'#4ade80':'var(--text-3)',transition:'background 0.3s',boxShadow:refreshPulse?'0 0 6px #4ade80':'none'}} />
              Updated {lastRefresh.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}
            </span>
          )}
          <button onClick={load} className="btn btn-secondary btn-sm">Refresh</button>
        </div>
      </div>
      <div className="page-content">

        {/* Real-time incident alerts */}
        {incidentAlerts.length > 0 && (
          <div style={{display:'flex',flexDirection:'column',gap:'0.5rem',marginBottom:'1rem'}}>
            {incidentAlerts.map(a => (
              <div key={a.id} style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.75rem 1rem',background:'rgba(220,38,38,0.12)',border:'2px solid rgba(220,38,38,0.4)',borderRadius:'8px',animation:'pulse 2s infinite'}}>
                <span style={{fontSize:'1.125rem'}}>⚠</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:'0.875rem',fontWeight:700,color:'#ef4444'}}>New incident logged</div>
                  <div style={{fontSize:'0.8125rem',color:'var(--text-2)'}}>{a.title} — {a.msg}</div>
                </div>
                <button onClick={() => setIncidentAlerts(prev => prev.filter(x => x.id !== a.id))}
                  style={{background:'none',border:'none',color:'rgba(220,38,38,0.5)',fontSize:'1.125rem',cursor:'pointer',padding:0}}>×</button>
              </div>
            ))}
          </div>
        )}

        {/* Compliance alerts — red/amber banners */}
        {alerts.length > 0 && (
          <div style={{display:'flex',flexDirection:'column',gap:'0.5rem',marginBottom:'1.25rem'}}>
            {alerts.map((a, i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.75rem 1rem',background:a.level==='warn'?'rgba(220,38,38,0.08)':'rgba(251,191,36,0.08)',border:`1px solid ${a.level==='warn'?'rgba(220,38,38,0.25)':'rgba(251,191,36,0.25)'}`,borderRadius:'8px'}}>
                <span style={{fontSize:'1rem'}}>{a.level==='warn'?'⚠':'ℹ'}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:'0.875rem',fontWeight:600,color:a.level==='warn'?'var(--danger)':'var(--warning)'}}>{a.msg}</div>
                  {a.site && <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginTop:'1px'}}>{a.site}</div>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Live site status — one card per active shift */}
        <div style={{marginBottom:'1.25rem'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.75rem'}}>
            <div style={{fontSize:'0.6875rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em'}}>
              Live — {data.shifts.length} officer{data.shifts.length!==1?'s':''} on duty
            </div>
            <Link to="/on-duty" style={{fontSize:'0.8125rem',color:'var(--blue)',textDecoration:'none',fontWeight:500}}>Full view →</Link>
          </div>
          {data.shifts.length === 0 ? (
            <div className="card" style={{textAlign:'center',padding:'1.5rem',color:'var(--text-3)'}}>No officers currently on duty</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:'0.625rem'}}>
              {data.shifts.map(s => (
                <div key={s.id} style={{display:'flex',alignItems:'center',gap:'0.875rem',padding:'0.875rem 1rem',background:'rgba(74,222,128,0.05)',border:'1px solid rgba(74,222,128,0.15)',borderRadius:'10px'}}>
                  <div style={{width:'10px',height:'10px',borderRadius:'50%',background:'#4ade80',flexShrink:0,boxShadow:'0 0 6px #4ade80'}} />
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:'0.9375rem',fontWeight:700}}>{s.officer?`${s.officer.first_name} ${s.officer.last_name}`:'Unknown'}</div>
                    <div style={{fontSize:'0.75rem',color:'var(--text-2)',marginTop:'1px'}}>
                      {s.site?.name||'—'} · {shiftDuration(s)} on duty
                    </div>
                  </div>
                  <Link to={`/sites/${s.site_id}`} className="btn btn-secondary btn-sm">Site</Link>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Incidents tonight — always prominent */}
        {data.incidents.length > 0 && (
          <div className="card" style={{marginBottom:'1.25rem',borderColor:'rgba(220,38,38,0.2)',background:'rgba(220,38,38,0.03)'}}>
            <div className="section-header" style={{marginBottom:'0.75rem'}}>
              <div className="section-title" style={{color:'var(--danger)'}}>⚠ Incidents — {data.incidents.length} recorded</div>
              <Link to="/logs" style={{fontSize:'0.8125rem',color:'var(--blue)',textDecoration:'none'}}>All logs →</Link>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
              {data.incidents.slice(0,5).map(l => (
                <div key={l.id} style={{padding:'0.625rem 0.75rem',background:'rgba(220,38,38,0.06)',border:'1px solid rgba(220,38,38,0.15)',borderRadius:'6px'}}>
                  <div style={{display:'flex',gap:'0.5rem',alignItems:'flex-start',justifyContent:'space-between'}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:'0.875rem',fontWeight:600,color:'var(--danger)'}}>{l.title||l.log_type}</div>
                      {l.description && <div style={{fontSize:'0.8125rem',color:'var(--text-2)',marginTop:'2px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{l.description}</div>}
                      <div style={{fontSize:'0.6875rem',color:'var(--text-3)',marginTop:'2px'}}>
                        {l.officer?`${l.officer.first_name} ${l.officer.last_name} · `:''}{l.site?.name||''} · {new Date(l.occurred_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}
                      </div>
                    </div>
                    {l.client_reportable && <span className="badge badge-warning" style={{flexShrink:0}}>Client</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats row */}
        <div className="stats-grid" style={{marginBottom:'1.25rem'}}>
          <Link to="/on-duty" className="stat-card" style={{cursor:'pointer',textDecoration:'none'}}><div className="stat-value">{data.shifts.length}</div><div className="stat-label">On Duty</div></Link>
          <div className="stat-card" style={{cursor:'pointer'}} onClick={() => setDashPanel(dashPanel === 'incidents' ? null : 'incidents')}><div className="stat-value" style={{color:data.incidents.length>0?'var(--danger)':'var(--text)'}}>{data.incidents.length}</div><div className="stat-label">Incidents Today</div></div>
          <div className="stat-card"><div className="stat-value">{data.logsToday||0}</div><div className="stat-label">Logs Today</div></div>
          <div className="stat-card" style={{cursor:'pointer'}} onClick={() => setDashPanel(dashPanel === 'tasksDue' ? null : 'tasksDue')}><div className="stat-value" style={{color:data.pendingTasks>0?'var(--warning)':'var(--text)'}}>{data.pendingTasks}</div><div className="stat-label">Tasks Due</div></div>
          <div className="stat-card" style={{cursor:'pointer'}} onClick={() => setDashPanel(dashPanel === 'tasksDone' ? null : 'tasksDone')}><div className="stat-value" style={{color:data.tasksDone>0?'#10b981':'var(--text)'}}>{data.tasksDone||0}</div><div className="stat-label">Tasks Done</div></div>
          <div className="stat-card"><div className="stat-value">{data.totalOfficers}</div><div className="stat-label">Officers</div></div>
        </div>

        {/* Client Tasks */}
        {clientTasks.length > 0 && (
          <div className="card" style={{marginBottom:'1.25rem',borderLeft:'3px solid #3b82f6'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
              <div className="section-title" style={{margin:0,color:'#3b82f6'}}>Client Tasks</div>
              <div style={{display:'flex',gap:'0.25rem'}}>
                {[
                  { label:'Open', value:'open', count: clientTasks.filter(t=>t.status==='open').length, color:'#3b82f6' },
                  { label:'Done', value:'resolved', count: clientTasks.filter(t=>t.status==='resolved').length, color:'#10b981' },
                  { label:'Failed', value:'unsuccessful', count: clientTasks.filter(t=>t.status==='unsuccessful').length, color:'var(--danger)' },
                  { label:'All', value:'', count: clientTasks.length },
                ].map(f => (
                  <button key={f.value} onClick={() => setCtFilter(f.value)}
                    style={{padding:'0.1875rem 0.5rem',borderRadius:'999px',border:`1px solid ${ctFilter===f.value ? (f.color||'#374151') : 'var(--border)'}`,
                      background:ctFilter===f.value ? (f.color||'#374151') : 'transparent',
                      color:ctFilter===f.value ? '#fff' : 'var(--text-3)',fontSize:'0.6875rem',fontWeight:600,cursor:'pointer'}}>
                    {f.label} {f.count > 0 ? `(${f.count})` : ''}
                  </button>
                ))}
              </div>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
              {clientTasks.filter(t => !ctFilter || t.status === ctFilter).map(t => {
                const borderColor = t.status==='resolved' ? '#10b981' : t.status==='unsuccessful' ? 'var(--danger)' : 'rgba(59,130,246,0.3)';
                const bgColor = t.status==='resolved' ? 'rgba(16,185,129,0.04)' : t.status==='unsuccessful' ? 'rgba(220,38,38,0.04)' : 'rgba(59,130,246,0.04)';
                let responses = [];
                try { const p = JSON.parse(t.description); if (Array.isArray(p)) responses = p; } catch {}
                return (
                  <div key={t.id} style={{padding:'0.75rem',background:bgColor,border:`1px solid ${borderColor}`,borderRadius:'8px'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:600,fontSize:'0.875rem'}}>{t.title}</div>
                        <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginTop:'0.25rem'}}>
                          {t.site?.name && <span style={{fontWeight:600}}>{t.site.name}</span>}
                          {' — '}{new Date(t.created_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})}
                        </div>
                      </div>
                      <span className={`badge ${t.status==='resolved'?'badge-success':t.status==='unsuccessful'?'badge-danger':'badge-blue'}`} style={{fontSize:'0.625rem'}}>
                        {t.status==='resolved'?'Complete':t.status==='unsuccessful'?'Unsuccessful':'Open'}
                      </span>
                    </div>
                    {responses.length > 0 && (
                      <div style={{marginTop:'0.5rem',paddingTop:'0.5rem',borderTop:'1px solid var(--border)'}}>
                        {responses.map((r,i) => (
                          <div key={i} style={{fontSize:'0.75rem',color:'var(--text-2)',padding:'0.125rem 0'}}>
                            <span style={{fontWeight:600}}>{r.name}</span>: {r.text}
                          </div>
                        ))}
                      </div>
                    )}
                    {t.status === 'open' && (
                      <div style={{display:'flex',gap:'0.375rem',marginTop:'0.5rem'}}>
                        <button onClick={() => { setCtModal({ task:t, outcome:'complete' }); setCtNotes(''); }} className="btn btn-sm" style={{background:'#10b981',color:'#fff',border:'none',borderRadius:'6px',padding:'0.375rem 0.75rem',fontSize:'0.75rem',fontWeight:700,cursor:'pointer'}}>Complete</button>
                        <button onClick={() => { setCtModal({ task:t, outcome:'unsuccessful' }); setCtNotes(''); }} className="btn btn-sm" style={{background:'rgba(220,38,38,0.1)',color:'var(--danger)',border:'1px solid rgba(220,38,38,0.25)',borderRadius:'6px',padding:'0.375rem 0.75rem',fontSize:'0.75rem',fontWeight:700,cursor:'pointer'}}>Unsuccessful</button>
                      </div>
                    )}
                  </div>
                );
              })}
              {clientTasks.filter(t => !ctFilter || t.status === ctFilter).length === 0 && (
                <div style={{padding:'1rem',textAlign:'center',color:'var(--text-3)',fontSize:'0.8125rem'}}>No {ctFilter || ''} tasks</div>
              )}
            </div>
          </div>
        )}

        {/* Client Task Response Modal */}
        {ctModal && (
          <div className="modal-overlay" onClick={() => setCtModal(null)}>
            <div className="modal" style={{maxWidth:'440px'}} onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title" style={{color: ctModal.outcome==='complete' ? '#10b981' : 'var(--danger)'}}>
                  {ctModal.outcome==='complete' ? 'Task Complete' : 'Task Unsuccessful'}
                </div>
                <button className="modal-close" onClick={() => setCtModal(null)}>x</button>
              </div>
              <div style={{marginBottom:'0.75rem',fontSize:'0.875rem',fontWeight:600}}>{ctModal.task.title}</div>
              <div className="field">
                <label className="label">{ctModal.outcome==='complete' ? 'Comments (optional)' : 'Reason *'}</label>
                <textarea className="input" rows={3} value={ctNotes} onChange={e => setCtNotes(e.target.value)}
                  placeholder={ctModal.outcome==='complete' ? 'Any notes on what was done...' : 'Why could this task not be completed?'} />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setCtModal(null)}>Cancel</button>
                <button className="btn btn-primary" disabled={ctSubmitting || (ctModal.outcome==='unsuccessful' && !ctNotes.trim())}
                  onClick={async () => {
                    setCtSubmitting(true);
                    try {
                      const name = `${user.first_name} ${user.last_name}`;
                      const existing = ctModal.task.description || '';
                      let responses = [];
                      try { const p = JSON.parse(existing); if (Array.isArray(p)) responses = p; } catch {}
                      responses.push({
                        from: 'manager', name, createdAt: new Date().toISOString(),
                        text: ctModal.outcome==='complete'
                          ? `✓ Task completed${ctNotes.trim() ? ': ' + ctNotes.trim() : ''}`
                          : `✗ Unsuccessful: ${ctNotes.trim()}`,
                      });
                      await api.alerts.update(ctModal.task.id, { status: ctModal.outcome==='complete' ? 'resolved' : 'unsuccessful', description: JSON.stringify(responses) });
                      setClientTasks(prev => prev.filter(x => x.id !== ctModal.task.id));
                      setCtModal(null);
                    } catch {}
                    setCtSubmitting(false);
                  }}>
                  {ctSubmitting ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Expandable panels */}
        {dashPanel === 'incidents' && (
          <div className="card" style={{marginBottom:'1.25rem'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
              <div className="section-title">Incidents Today</div>
              <button onClick={() => setDashPanel(null)} style={{background:'none',border:'none',color:'var(--text-3)',cursor:'pointer',fontSize:'1rem'}}>×</button>
            </div>
            {data.incidents.length === 0 ? <div style={{color:'var(--text-3)',fontSize:'0.875rem'}}>No incidents today</div> : (
              <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
                {data.incidents.map(l => (
                  <div key={l.id} onClick={() => setReviewLog(l)} style={{padding:'0.625rem 0.75rem',background:'rgba(220,38,38,0.06)',border:'1px solid rgba(220,38,38,0.15)',borderRadius:'8px',cursor:'pointer'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                      <div style={{fontWeight:600,fontSize:'0.875rem',color:'#ef4444'}}>{l.title || l.log_type}</div>
                      <span style={{fontSize:'0.6875rem',padding:'2px 8px',borderRadius:'4px',fontWeight:600,background: l.review_status === 'RESOLVED' ? 'rgba(16,185,129,0.15)' : l.review_status === 'UNDER_REVIEW' ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.08)',color: l.review_status === 'RESOLVED' ? '#10b981' : l.review_status === 'UNDER_REVIEW' ? '#60a5fa' : 'var(--text-3)'}}>{l.review_status || 'NEW'}</span>
                    </div>
                    <div style={{fontSize:'0.8125rem',color:'var(--text-2)',marginTop:'2px'}}>{l.description?.slice(0,100)}</div>
                    <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginTop:'4px'}}>{l.site?.name} · {l.officer?.first_name} {l.officer?.last_name} · {new Date(l.occurred_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {dashPanel === 'tasksDue' && (
          <div className="card" style={{marginBottom:'1.25rem'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
              <div className="section-title">Tasks Due</div>
              <button onClick={() => setDashPanel(null)} style={{background:'none',border:'none',color:'var(--text-3)',cursor:'pointer',fontSize:'1rem'}}>×</button>
            </div>
            {tasksDueList.length === 0 ? <div style={{color:'var(--text-3)',fontSize:'0.875rem'}}>No tasks due</div> : (
              <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
                {tasksDueList.map(t => (
                  <div key={t.id} style={{padding:'0.625rem 0.75rem',background:'rgba(251,191,36,0.06)',border:'1px solid rgba(251,191,36,0.15)',borderRadius:'8px'}}>
                    <div style={{fontWeight:600,fontSize:'0.875rem'}}>{t.title}</div>
                    {t.description && <div style={{fontSize:'0.8125rem',color:'var(--text-2)',marginTop:'2px'}}>{t.description?.slice(0,100)}</div>}
                    <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginTop:'4px'}}>{t.site?.name} · {t.assigned_to_name || 'Unassigned'} · Due {t.due_date ? new Date(t.due_date).toLocaleDateString('en-GB') : '—'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {dashPanel === 'tasksDone' && (
          <div className="card" style={{marginBottom:'1.25rem'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
              <div className="section-title">Tasks Completed Today</div>
              <button onClick={() => setDashPanel(null)} style={{background:'none',border:'none',color:'var(--text-3)',cursor:'pointer',fontSize:'1rem'}}>×</button>
            </div>
            {tasksDoneList.length === 0 ? <div style={{color:'var(--text-3)',fontSize:'0.875rem'}}>No tasks completed today</div> : (
              <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
                {tasksDoneList.map(l => (
                  <div key={l.id} style={{padding:'0.625rem 0.75rem',background:'rgba(16,185,129,0.06)',border:'1px solid rgba(16,185,129,0.15)',borderRadius:'8px'}}>
                    <div style={{fontWeight:600,fontSize:'0.875rem',color:'#10b981'}}>{l.title}</div>
                    {l.description && <div style={{fontSize:'0.8125rem',color:'var(--text-2)',marginTop:'2px'}}>{l.description?.slice(0,100)}</div>}
                    <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginTop:'4px'}}>{l.site?.name} · {l.officer?.first_name} {l.officer?.last_name} · {new Date(l.occurred_at).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Recent activity + quick actions */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1.25rem'}}>
          <div className="card">
            <div className="section-header" style={{marginBottom:'0.75rem'}}>
              <div className="section-title">Recent Activity</div>
              <Link to="/logs" style={{fontSize:'0.8125rem',color:'var(--blue)',textDecoration:'none'}}>All →</Link>
            </div>
            {data.recentLogs.length === 0 ? <div className="empty-state"><p>No logs yet</p></div> : (
              <div>{data.recentLogs.map(log => <ManagerLogPreview key={log.id} log={log} />)}</div>
            )}
          </div>
          <div className="card">
            <div className="section-title" style={{marginBottom:'0.75rem'}}>Quick Actions</div>
            <div style={{display:'flex',flexDirection:'column',gap:'0.5rem'}}>
              <Link to="/logs" className="btn btn-secondary" style={{justifyContent:'flex-start',gap:'0.5rem'}}><EyeIcon style={{width:'1rem',height:'1rem'}} /> Daily Occurrence Books</Link>
              <Link to="/on-duty" className="btn btn-secondary" style={{justifyContent:'flex-start',gap:'0.5rem'}}><UsersIcon style={{width:'1rem',height:'1rem'}} /> Officers On Duty</Link>
              <Link to="/sites" className="btn btn-secondary" style={{justifyContent:'flex-start',gap:'0.5rem'}}><BuildingOfficeIcon style={{width:'1rem',height:'1rem'}} /> Site Locations</Link>
              <Link to="/reports" className="btn btn-secondary" style={{justifyContent:'flex-start',gap:'0.5rem'}}><ChartBarIcon style={{width:'1rem',height:'1rem'}} /> Reports</Link>
              <Link to="/roster" className="btn btn-secondary" style={{justifyContent:'flex-start',gap:'0.5rem'}}><ClockIcon style={{width:'1rem',height:'1rem'}} /> Roster</Link>
            </div>
          </div>
        </div>
      </div>
      {reviewLog && <IncidentReviewModal log={reviewLog} user={user} onClose={() => setReviewLog(null)} onUpdated={(updated) => { setReviewLog(null); load(); }} />}
    </div>
  );
}

// ── Incident Review Modal ───────────────────────────────────────────────────
function IncidentReviewModal({ log, user, onClose, onUpdated }) {
  const [status, setStatus] = useState(log.review_status || 'NEW');
  const [nextAction, setNextAction] = useState(log.next_action || '');
  const [resolution, setResolution] = useState(log.resolution || '');
  const [clientInformed, setClientInformed] = useState(log.client_informed || false);
  const [moreDetails, setMoreDetails] = useState(log.more_details_required || false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL || 'https://dob-live-api.onrender.com'}/api/logs/${log.id}/comments`, {
      headers: { Authorization: `Bearer ${window.__clerkGetToken ? '' : ''}` }
    });
    api.logs.list({ limit: 1 }).then(() => {});
    // Load comments
    (async () => {
      try {
        const token = await window.__clerkGetToken?.() || '';
        const API = import.meta.env.VITE_API_URL || 'https://dob-live-api.onrender.com';
        const res = await fetch(`${API}/api/logs/${log.id}/comments`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        setComments(data.data || []);
      } catch {}
      finally { setLoadingComments(false); }
    })();
  }, [log.id]);

  async function save() {
    setSaving(true);
    try {
      const token = await window.__clerkGetToken?.() || '';
      const API = import.meta.env.VITE_API_URL || 'https://dob-live-api.onrender.com';
      await fetch(`${API}/api/logs/${log.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          review_status: status, next_action: nextAction || null, resolution: resolution || null,
          client_informed: clientInformed, client_informed_at: clientInformed ? new Date().toISOString() : null,
          more_details_required: moreDetails,
          reviewed_by: user.id, reviewed_at: new Date().toISOString(),
        }),
      });
      onUpdated();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function addComment() {
    if (!newComment.trim()) return;
    try {
      const token = await window.__clerkGetToken?.() || '';
      const API = import.meta.env.VITE_API_URL || 'https://dob-live-api.onrender.com';
      const res = await fetch(`${API}/api/logs/${log.id}/comments`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ comment: newComment.trim() }),
      });
      const data = await res.json();
      if (data.data) setComments(prev => [...prev, data.data]);
      setNewComment('');
    } catch (e) { alert(e.message); }
  }

  const inp = { width:'100%', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'6px', padding:'0.5rem 0.75rem', fontSize:'0.875rem', color:'var(--text)', boxSizing:'border-box', fontFamily:'inherit' };
  const lbl = { fontSize:'0.6875rem', fontWeight:600, color:'var(--text-3)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'0.375rem', display:'block' };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:'600px',maxHeight:'90vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title" style={{color:'#ef4444'}}>Incident Review</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Incident details */}
        <div style={{padding:'0.75rem',background:'rgba(220,38,38,0.06)',border:'1px solid rgba(220,38,38,0.15)',borderRadius:'8px',marginBottom:'1rem'}}>
          <div style={{fontWeight:700,fontSize:'1rem',color:'#ef4444',marginBottom:'0.25rem'}}>{log.title || log.log_type}</div>
          <div style={{fontSize:'0.875rem',color:'var(--text)',lineHeight:1.5,marginBottom:'0.5rem'}}>{log.description}</div>
          <div style={{fontSize:'0.75rem',color:'var(--text-3)'}}>
            {log.site?.name} · {log.officer?.first_name} {log.officer?.last_name} · {new Date(log.occurred_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})}
          </div>
        </div>

        {/* Review fields */}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem',marginBottom:'1rem'}}>
          <div>
            <label style={lbl}>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)} style={inp}>
              <option value="NEW">New</option>
              <option value="UNDER_REVIEW">Under Review</option>
              <option value="RESOLVED">Resolved</option>
              <option value="UNRESOLVED">Unresolved</option>
            </select>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:'0.5rem',justifyContent:'flex-end'}}>
            <label style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer',fontSize:'0.875rem'}}>
              <input type="checkbox" checked={clientInformed} onChange={e => setClientInformed(e.target.checked)} style={{width:'1rem',height:'1rem',accentColor:'var(--blue)'}} />
              Client Informed
            </label>
            <label style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer',fontSize:'0.875rem'}}>
              <input type="checkbox" checked={moreDetails} onChange={e => setMoreDetails(e.target.checked)} style={{width:'1rem',height:'1rem',accentColor:'#f59e0b'}} />
              More Details Required
            </label>
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <label style={lbl}>Next Action</label>
            <input value={nextAction} onChange={e => setNextAction(e.target.value)} placeholder="What needs to happen next..." style={inp} />
          </div>
          <div style={{gridColumn:'1/-1'}}>
            <label style={lbl}>Resolution</label>
            <textarea value={resolution} onChange={e => setResolution(e.target.value)} rows={2} placeholder="Final outcome..." style={{...inp, resize:'none'}} />
          </div>
        </div>

        {/* Comments */}
        <div style={{borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginBottom:'0.75rem'}}>
          <div style={{...lbl,marginBottom:'0.5rem'}}>Comments</div>
          {loadingComments ? <div style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>Loading...</div> : (
            <div style={{display:'flex',flexDirection:'column',gap:'0.375rem',marginBottom:'0.75rem',maxHeight:'200px',overflowY:'auto'}}>
              {comments.length === 0 && <div style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>No comments yet</div>}
              {comments.map(c => (
                <div key={c.id} style={{padding:'0.5rem 0.625rem',background:'var(--surface)',borderRadius:'6px',border:'1px solid var(--border)'}}>
                  <div style={{fontSize:'0.8125rem'}}>{c.comment}</div>
                  <div style={{fontSize:'0.6875rem',color:'var(--text-3)',marginTop:'2px'}}>{c.user?.first_name} {c.user?.last_name} · {new Date(c.created_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'})}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{display:'flex',gap:'0.5rem'}}>
            <input value={newComment} onChange={e => setNewComment(e.target.value)} onKeyDown={e => e.key === 'Enter' && addComment()} placeholder="Add a comment..." style={{...inp,flex:1}} />
            <button onClick={addComment} disabled={!newComment.trim()} className="btn btn-primary btn-sm">Post</button>
          </div>
        </div>

        <div className="modal-footer" style={{display:'flex',gap:'0.5rem',flexWrap:'wrap'}}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-secondary" onClick={async () => {
            try {
              const res = await api.report.pdf(log.id);
              if (res.pdf) {
                const blob = new Blob([Uint8Array.from(atob(res.pdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href = url; a.download = res.filename || 'incident-report.pdf'; a.click();
                URL.revokeObjectURL(url);
              }
            } catch (err) { alert('PDF generation failed: ' + err.message); }
          }}>Download PDF</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Review'}</button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, color }) {
  return (
    <div className="stat-card">
      <div className="stat-value">{value}</div>
      <div className="stat-label">{title}</div>
    </div>
  );
}


function ManagerLogPreview({ log }) {
  const typeColors = {
    INCIDENT:'badge-danger', ALARM:'badge-warning', PATROL:'badge-blue', GENERAL:'badge-neutral',
  };
  return (
    <div style={{display:'flex',gap:'0.75rem',alignItems:'flex-start',padding:'0.5rem 0',borderBottom:'1px solid var(--border)'}}>
      <span className={`badge ${typeColors[log.log_type]||'badge-neutral'}`} style={{flexShrink:0,marginTop:'2px'}}>{log.log_type}</span>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontSize:'0.875rem',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{log.title || 'Log Entry'}</div>
        <div style={{fontSize:'0.75rem',color:'var(--text-2)'}}>
          {log.officer ? `${log.officer.first_name} ${log.officer.last_name}` : ''}{log.site ? ` · ${log.site.name}` : ''}
          {' · '}{new Date(log.occurred_at).toLocaleDateString('en-GB')}
        </div>
      </div>
    </div>
  );
}


function ManagerActionButton({ to, icon, title, subtitle }) {
  return (
    <Link to={to} style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.75rem',border:'1px solid var(--border)',borderRadius:'var(--radius)',textDecoration:'none',color:'var(--text)',background:'var(--surface)',transition:'background 0.15s'}}
      onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'}
      onMouseLeave={e=>e.currentTarget.style.background='var(--surface)'}
    >
      <span style={{color:'var(--blue)'}}>{icon}</span>
      <div>
        <div style={{fontSize:'0.875rem',fontWeight:500}}>{title}</div>
        {subtitle && <div style={{fontSize:'0.75rem',color:'var(--text-2)'}}>{subtitle}</div>}
      </div>
    </Link>
  );
}


function SiteManagement({ user }) {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editSite, setEditSite] = useState(null);
  const [portalSite, setPortalSite] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  async function load() {
    try {
      const res = await api.sites.list();
      setSites(res.data || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Sites</div>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditSite(null); setShowForm(true); }}>
          <PlusIcon style={{width:'0.875rem',height:'0.875rem'}} /> Add Site
        </button>
      </div>
      <div className="page-content">
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
        ) : sites.length === 0 ? (
          <div className="empty-state"><p>No sites yet. Add your first site.</p></div>
        ) : (() => {
          // Group sites by client_name
          const grouped = {};
          const ungrouped = [];
          sites.forEach(site => {
            if (site.client_name) {
              if (!grouped[site.client_name]) grouped[site.client_name] = [];
              grouped[site.client_name].push(site);
            } else {
              ungrouped.push(site);
            }
          });
          const clientNames = Object.keys(grouped).sort();
          const SiteRow = ({ site, indent }) => (
            <tr key={site.id}>
              <td style={{fontWeight:500,paddingLeft:indent?'2rem':undefined}}>
                {indent && <span style={{color:'var(--text-3)',marginRight:'0.5rem'}}>└</span>}
                <Link to={`/sites/${site.id}`} style={{color:'var(--text)',textDecoration:'none',fontWeight:600}}>{site.name}</Link>
              </td>
              <td style={{color:'var(--text-2)'}}>{site.address || '—'}</td>
              <td>
                <span className={`badge ${site.active !== false ? 'badge-success' : 'badge-neutral'}`}>
                  {site.active !== false ? 'Active' : 'Inactive'}
                </span>
              </td>
              <td style={{textAlign:'right',display:'flex',gap:'0.5rem',justifyContent:'flex-end'}}>
                <Link to={`/sites/${site.id}`} className="btn btn-ghost btn-sm" style={{color:"var(--blue)"}}>Configure</Link>
                <button className="btn btn-ghost btn-sm" onClick={() => { setEditSite(site); setShowForm(true); }}>Edit</button>
                <button className="btn btn-ghost btn-sm" onClick={() => setPortalSite(site)}>Portal</button>
                <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={async () => {
                  if (!window.confirm(`Delete "${site.name}"? This cannot be undone.`)) return;
                  try { await api.sites.delete(site.id); load(); } catch(e) { alert(e.message); }
                }}>Delete</button>
              </td>
            </tr>
          );
          return (
            <table className="table">
              <thead>
                <tr>
                  <th>Site Name</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {clientNames.map(client => (
                  <React.Fragment key={client}>
                    <tr>
                      <td colSpan={4} style={{background:'var(--surface-2)',padding:'0.625rem 1rem',borderBottom:'1px solid var(--border)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                          <BuildingOfficeIcon style={{width:'1rem',height:'1rem',color:'var(--blue)'}} />
                          <span style={{fontWeight:700,fontSize:'0.875rem',color:'var(--text-1)'}}>{client}</span>
                          <span style={{fontSize:'0.75rem',color:'var(--text-3)',marginLeft:'0.25rem'}}>{grouped[client].length} site{grouped[client].length!==1?'s':''}</span>
                        </div>
                      </td>
                    </tr>
                    {grouped[client].map(site => <SiteRow key={site.id} site={site} indent />)}
                  </React.Fragment>
                ))}
                {ungrouped.map(site => <SiteRow key={site.id} site={site} />)}
              </tbody>
            </table>
          );
        })()}
      </div>
      {showForm && (
        <SiteFormModal
          site={editSite}
          onClose={() => setShowForm(false)}
          onSaved={(msg) => { setShowForm(false); load(); if (msg) { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(null), 5000); } }}
        />
      )}
      {portalSite && (
        <PortalSettingsModal
          site={portalSite}
          onClose={() => setPortalSite(null)}
          onSaved={() => { setPortalSite(null); load(); }}
        />
      )}
    </div>
  );
}

function SiteFormModal({ site, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:                    site?.name || '',
    address:                 site?.address || '',
    city:                    site?.city || '',
    postcode:                site?.postcode || '',
    contact_name:            site?.contact_name || '',
    contact_phone:           site?.contact_phone || '',
    contact_email:           site?.contact_email || '',
    escalation_contact_1_name:   site?.escalation_contact_1_name || '',
    escalation_contact_1_mobile: site?.escalation_contact_1_mobile || '',
    escalation_contact_2_name:   site?.escalation_contact_2_name || '',
    escalation_contact_2_mobile: site?.escalation_contact_2_mobile || '',
    geofence_lat:            site?.geofence_lat || '',
    geofence_lng:            site?.geofence_lng || '',
    geofence_radius:         site?.geofence_radius || 500,
    notes:                   site?.notes || '',
    active:                  site?.active !== false,
    client_name:             site?.client_name || '',
    contract_start_date:     site?.contract_start_date || '',
    client_company_address:  site?.client_company_address || '',
    service_types:           site?.service_types || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const f = (k, v) => setForm(p => ({...p, [k]: v}));

  async function save() {
    if (!form.name.trim()) { setError('Site name is required'); return; }
    try {
      setSaving(true);
      const payload = {
        ...form,
        geofence_lat: form.geofence_lat ? parseFloat(form.geofence_lat) : null,
        geofence_lng: form.geofence_lng ? parseFloat(form.geofence_lng) : null,
        geofence_radius: parseInt(form.geofence_radius) || 500,
      };
      if (site) await api.sites.update(site.id, payload);
      else await api.sites.create(payload);
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:'640px'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{site ? 'Edit Site' : 'Add Site'}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}

        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field" style={{gridColumn:'1/-1'}}>
            <label className="label">Site Name *</label>
            <input className="input" value={form.name} onChange={e=>f('name',e.target.value)} placeholder="e.g. Brindleyplace" />
          </div>
          <div className="field" style={{gridColumn:'1/-1'}}>
            <label className="label">Address</label>
            <input className="input" value={form.address} onChange={e=>f('address',e.target.value)} placeholder="Street address" />
          </div>
          <div className="field">
            <label className="label">City</label>
            <input className="input" value={form.city} onChange={e=>f('city',e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Postcode</label>
            <input className="input" value={form.postcode} onChange={e=>f('postcode',e.target.value)} />
          </div>

          <div className="field" style={{gridColumn:'1/-1'}}>
            <label className="label">Client / Group Name</label>
            <input className="input" value={form.client_name} onChange={e=>f('client_name',e.target.value)} placeholder="e.g. Aldi Stores Ltd — leave blank for ungrouped" />
          </div>

          <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}>
            <div className="section-title" style={{marginBottom:'0.5rem'}}>Service Types</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.375rem'}}>
              {['Static','Mobile Patrols','Alarm Response & Keyholding','CCTV Hire','CCTV Monitoring','Property Inspection'].map(st => (
                <label key={st} style={{display:'flex',alignItems:'center',gap:'0.5rem',padding:'0.5rem 0.625rem',background: form.service_types.includes(st) ? 'rgba(26,82,168,0.06)' : 'var(--surface-2)',border: form.service_types.includes(st) ? '1px solid rgba(26,82,168,0.25)' : '1px solid var(--border)',borderRadius:'6px',cursor:'pointer',fontSize:'0.8125rem',color:'var(--text)'}}>
                  <input type="checkbox" checked={form.service_types.includes(st)}
                    onChange={() => f('service_types', form.service_types.includes(st) ? form.service_types.filter(s => s !== st) : [...form.service_types, st])}
                    style={{accentColor:'var(--blue)'}} />
                  {st}
                </label>
              ))}
            </div>
          </div>

          <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}>
            <div className="section-title" style={{marginBottom:'0.5rem'}}>Site Contact</div>
          </div>
          <div className="field">
            <label className="label">Contact Name</label>
            <input className="input" value={form.contact_name} onChange={e=>f('contact_name',e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Contact Phone</label>
            <input className="input" value={form.contact_phone} onChange={e=>f('contact_phone',e.target.value)} />
          </div>
          <div className="field" style={{gridColumn:'1/-1'}}>
            <label className="label">Contact Email</label>
            <input type="email" className="input" value={form.contact_email} onChange={e=>f('contact_email',e.target.value)} />
          </div>

          <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}>
            <div className="section-title" style={{marginBottom:'0.5rem'}}>Escalation Contacts</div>
          </div>
          <div className="field">
            <label className="label">Escalation 1 Name</label>
            <input className="input" value={form.escalation_contact_1_name} onChange={e=>f('escalation_contact_1_name',e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Escalation 1 Mobile</label>
            <input className="input" value={form.escalation_contact_1_mobile} onChange={e=>f('escalation_contact_1_mobile',e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Escalation 2 Name</label>
            <input className="input" value={form.escalation_contact_2_name} onChange={e=>f('escalation_contact_2_name',e.target.value)} />
          </div>
          <div className="field">
            <label className="label">Escalation 2 Mobile</label>
            <input className="input" value={form.escalation_contact_2_mobile} onChange={e=>f('escalation_contact_2_mobile',e.target.value)} />
          </div>

          <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}>
            <div className="section-title" style={{marginBottom:'0.5rem'}}>Client</div>
          </div>
          <div className="field">
            <label className="label">Contract Start Date</label>
            <input type="date" className="input" value={form.contract_start_date} onChange={e=>f('contract_start_date',e.target.value)} />
          </div>
          <div className="field" style={{gridColumn:'1/-1'}}>
            <label className="label">Client Company Address</label>
            <input className="input" value={form.client_company_address} onChange={e=>f('client_company_address',e.target.value)} placeholder="Client's registered address" />
          </div>

          <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}>
            <div className="section-title" style={{marginBottom:'0.5rem'}}>Geofence</div>
          </div>
          <div className="field">
            <label className="label">Latitude</label>
            <input type="number" step="any" className="input" value={form.geofence_lat} onChange={e=>f('geofence_lat',e.target.value)} placeholder="e.g. 52.4862" />
          </div>
          <div className="field">
            <label className="label">Longitude</label>
            <input type="number" step="any" className="input" value={form.geofence_lng} onChange={e=>f('geofence_lng',e.target.value)} placeholder="e.g. -1.8904" />
          </div>
          <div className="field">
            <label className="label">Radius (metres)</label>
            <input type="number" className="input" value={form.geofence_radius} onChange={e=>f('geofence_radius',e.target.value)} placeholder="500" />
          </div>

          <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}>
            <label className="label">Notes</label>
            <textarea className="input" rows={3} value={form.notes} onChange={e=>f('notes',e.target.value)} />
          </div>
          <div className="field">
            <label style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer'}}>
              <input type="checkbox" checked={form.active} onChange={e=>f('active',e.target.checked)} style={{width:'1rem',height:'1rem',accentColor:'var(--blue)'}} />
              <span className="label" style={{margin:0}}>Active site</span>
            </label>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving...':'Save'}</button>
        </div>
      </div>
    </div>
  );
}



function LogReview({ user }) {
  const [logs, setLogs] = useState([]);
  const [sites, setSites] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [officerFilter, setOfficerFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const params = { limit: 500 };
        if (dateFrom) params.from = new Date(dateFrom + 'T00:00:00').toISOString();
        if (dateTo) params.to = new Date(dateTo + 'T23:59:59').toISOString();
        const [logsRes, sitesRes, usersRes] = await Promise.all([
          api.logs.list(params),
          api.sites.list(),
          api.users.list(),
        ]);
        setLogs(logsRes.data || []);
        setSites(sitesRes.data || []);
        setOfficers((usersRes.data || []).filter(u => u.role === 'OFFICER').sort((a,b) => (a.last_name||'').localeCompare(b.last_name||'')));
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    }
    load();
  }, [dateFrom, dateTo]);

  const logTypes = ['PATROL','INCIDENT','ALARM','ACCESS','VISITOR','HANDOVER','MAINTENANCE','VEHICLE','GENERAL','CCTV_CHECK','WELFARE_CHECK'];

  const filtered = logs.filter(l => {
    if (l.type_data?.checkpoint) return false;
    if (typeFilter && l.log_type !== typeFilter) return false;
    if (siteFilter && l.site_id !== siteFilter) return false;
    if (officerFilter && l.officer_id !== officerFilter) return false;
    if (filter) {
      const q = filter.toLowerCase();
      return (l.title || '').toLowerCase().includes(q) ||
             (l.description || '').toLowerCase().includes(q) ||
             (l.officer?.first_name || '').toLowerCase().includes(q) ||
             (l.officer?.last_name || '').toLowerCase().includes(q) ||
             (l.site?.name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const typeColors = {
    INCIDENT: 'badge-danger', ALARM: 'badge-warning',
    PATROL: 'badge-blue', GENERAL: 'badge-neutral',
    ACCESS: 'badge-navy', VISITOR: 'badge-navy',
    HANDOVER: 'badge-success', MAINTENANCE: 'badge-neutral',
    VEHICLE: 'badge-neutral', WELFARE: 'badge-blue', WELFARE_CHECK: 'badge-success', KEYHOLDING: 'badge-navy',
  };

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Log Review</div>
        <div style={{display:'flex',gap:'0.5rem',alignItems:'center',flexWrap:'wrap'}}>
          <input className="input" style={{width:'150px'}} placeholder="Search..." value={filter} onChange={e => setFilter(e.target.value)} />
          <select className="input" style={{width:'140px'}} value={siteFilter} onChange={e => setSiteFilter(e.target.value)}>
            <option value="">All sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className="input" style={{width:'130px'}} value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="">All types</option>
            {logTypes.map(t => <option key={t} value={t}>{t.charAt(0)+t.slice(1).toLowerCase().replace(/_/g,' ')}</option>)}
          </select>
          <select className="input" style={{width:'140px'}} value={officerFilter} onChange={e => setOfficerFilter(e.target.value)}>
            <option value="">All officers</option>
            {officers.map(o => <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>)}
          </select>
          <input type="date" className="input" style={{width:'130px'}} value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" />
          <input type="date" className="input" style={{width:'130px'}} value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" />
        </div>
      </div>
      <div className="page-content">
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><p>No logs found</p></div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Type</th>
                <th>Title</th>
                <th>Officer</th>
                <th>Site</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(log => (
                <tr key={log.id} onClick={() => setSelectedLog(log)} style={{cursor:'pointer', background: log.type_data?.duress ? 'rgba(239,68,68,0.15)' : log.type_data?.panic ? 'rgba(239,68,68,0.1)' : log.type_data?.missed_check ? 'rgba(245,158,11,0.1)' : log.type_data?.shift_event === 'OFF_DUTY' ? 'rgba(239,68,68,0.08)' : log.type_data?.shift_event === 'ON_DUTY' ? 'rgba(59,130,246,0.08)' : log.type_data?.check_call ? 'rgba(16,185,129,0.06)' : undefined}}>
                  <td style={{color:'var(--text-2)',whiteSpace:'nowrap',fontSize:'0.8125rem'}}>
                    {new Date(log.occurred_at).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'2-digit'})}
                    {' '}
                    {new Date(log.occurred_at).toLocaleTimeString('en-GB', {hour:'2-digit',minute:'2-digit'})}
                  </td>
                  <td><span className={`badge ${typeColors[log.log_type] || 'badge-neutral'}`}>{log.log_type}</span></td>
                  <td style={{fontWeight:500,maxWidth:'240px'}}>
                    <div style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{log.title || '—'}</div>
                    {log.type_data?.duress && <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#ef4444',background:'rgba(239,68,68,0.15)',display:'inline-block',padding:'1px 6px',borderRadius:'3px',marginBottom:'2px'}}>🚨 DURESS</div>}
                    {log.description && <div style={{fontSize:'0.75rem',color:'var(--text-2)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{log.description}</div>}
                    {(log.type_data?.media?.length > 0) && <div style={{fontSize:'0.7rem',color:'var(--blue)'}}>{log.type_data.media.length} photo{log.type_data.media.length>1?'s':''} attached</div>}
                  </td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>
                    {log.officer ? `${log.officer.first_name} ${log.officer.last_name}` : '—'}
                  </td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{log.site?.name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    {selectedLog && (
      selectedLog.log_type === 'PATROL' && selectedLog.type_data?.patrol_session_id
        ? <PatrolDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
        : <LogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    )}
    </div>
  );
}


function ManagerLogCard({ log }) {
  const [showPatrolDetail, setShowPatrolDetail] = useState(false);
  const [showLogDetail, setShowLogDetail] = useState(false);

  const typeMap = {
    PATROL:'PAT',INCIDENT:'INC',ALARM:'ALM',ACCESS:'ACC',VISITOR:'VIS',
    HANDOVER:'HND',MAINTENANCE:'MNT',VEHICLE:'VEH',KEYHOLDING:'KEY',GENERAL:'GEN',
  };
  const code = typeMap[log.log_type] || (log.log_type?.slice(0,3) || 'LOG');
  const typeColors = { INCIDENT:'badge-danger', ALARM:'badge-warning', PATROL:'badge-blue', GENERAL:'badge-neutral' };

  const isPatrolLog = log.log_type === 'PATROL' && log.type_data?.patrol_session_id;
  const media = log.type_data?.media || [];

  return (
    <>
    <div onClick={() => isPatrolLog ? setShowPatrolDetail(true) : setShowLogDetail(true)}
      style={{padding:'0.75rem',border:'1px solid var(--border)',borderRadius:'var(--radius)',marginBottom:'0.5rem',cursor:'pointer'}}>
      <div style={{display:'flex',alignItems:'flex-start',gap:'0.75rem'}}>
        <div style={{width:'2.25rem',height:'2.25rem',background:'var(--navy)',color:'#fff',borderRadius:'5px',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.5625rem',fontWeight:700,letterSpacing:'0.03em',flexShrink:0}}>{code}</div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:'0.5rem',flexWrap:'wrap',marginBottom:'0.25rem'}}>
            <span style={{fontWeight:600,fontSize:'0.875rem'}}>{log.title || 'Log Entry'}</span>
            <span className={`badge ${typeColors[log.log_type]||'badge-neutral'}`}>{log.log_type}</span>
            <span style={{fontSize:'0.75rem',color:'var(--text-2)'}}>{log.occurred_at ? new Date(log.occurred_at).toLocaleString('en-GB',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'}) : ''}</span>
          </div>
          <div style={{display:'flex',gap:'1rem',fontSize:'0.8125rem',color:'var(--text-2)',marginBottom:log.description?'0.375rem':0}}>
            {log.site && <span>{log.site.name}</span>}
            {log.officer && <span>{log.officer.first_name} {log.officer.last_name}</span>}
          </div>
          {log.description && (
            <p style={{fontSize:'0.875rem',color:'var(--text-2)',lineHeight:1.5,margin:'0 0 0.25rem'}}>
              {log.description.length > 120 ? log.description.substring(0,120)+'...' : log.description}
            </p>
          )}
          {media.length > 0 && (
            <div style={{fontSize:'0.75rem',color:'var(--blue)',marginTop:'0.25rem'}}>{media.length} photo{media.length>1?'s':''} attached · Click to view →</div>
          )}
          {isPatrolLog && log.type_data.duration_minutes != null && (
            <div style={{fontSize:'0.75rem',color:'var(--blue)',marginTop:'0.25rem'}}>
              {log.type_data.duration_minutes >= 60 ? `${Math.floor(log.type_data.duration_minutes/60)}h ${log.type_data.duration_minutes%60}m` : `${log.type_data.duration_minutes}m`}
              {' · '}{log.type_data.checkpoints_completed?.length || 0} checkpoints · Click for detail →
            </div>
          )}
          {!isPatrolLog && <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginTop:'0.25rem'}}>Click to view full details →</div>}
        </div>
      </div>
    </div>
    {showPatrolDetail && <PatrolDetailModal log={log} onClose={() => setShowPatrolDetail(false)} />}
    {showLogDetail && <LogDetailModal log={log} onClose={() => setShowLogDetail(false)} />}
    </>
  );
}

// ── Log Detail Modal (non-patrol logs) ───────────────────────────────────────
function LogDetailModal({ log, onClose }) {
  const typeColors = { INCIDENT:'badge-danger', ALARM:'badge-warning', PATROL:'badge-blue', GENERAL:'badge-neutral' };
  const td = log.type_data || {};
  const media = td.media || [];
  const [lightbox, setLightbox] = useState(null);

  const fmtDateTime = t => t ? new Date(t).toLocaleString('en-GB', { weekday:'short', day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit', timeZone:'Europe/London' }) : '—';

  // Collect any extra type_data fields worth showing
  const skipKeys = ['media','patrol_session_id','checkpoint','photo_url'];
  const extraFields = Object.entries(td).filter(([k]) => !skipKeys.includes(k) && td[k] != null && td[k] !== '');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:'600px',maxHeight:'90vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
            <div className="modal-title">{log.title || 'Log Entry'}</div>
            <span className={`badge ${typeColors[log.log_type]||'badge-neutral'}`}>{log.log_type}</span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Meta */}
        <div style={{display:'flex',gap:'1rem',flexWrap:'wrap',marginBottom:'1rem',padding:'0.75rem',background:'var(--surface-2)',borderRadius:'6px'}}>
          <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase',marginBottom:'2px'}}>Officer</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{log.officer ? `${log.officer.first_name} ${log.officer.last_name}` : '—'}</div></div>
          <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase',marginBottom:'2px'}}>Site</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{log.site?.name || '—'}</div></div>
          <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase',marginBottom:'2px'}}>Date &amp; Time</div><div style={{fontSize:'0.875rem'}}>{fmtDateTime(log.occurred_at)}</div></div>
          {log.client_reportable && <div style={{alignSelf:'center'}}><span className="badge badge-blue">Client Reportable</span></div>}
        </div>

        {/* Incident details */}
        {log.log_type === 'INCIDENT' && (
          <div style={{marginBottom:'1rem',padding:'0.75rem',background:'var(--surface-2)',borderRadius:'8px',border:'1px solid rgba(239,68,68,0.2)'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'#ef4444',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Incident Details</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem'}}>
              {td.incident_type && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Type</div><div style={{fontWeight:700,fontSize:'0.9375rem',color:'#ef4444'}}>{td.incident_type}</div></div>}
              {(td.incident_date || td.incident_time) && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Time of Incident</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{td.incident_date ? new Date(td.incident_date+'T12:00:00').toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}) : ''}{td.incident_time ? ` at ${td.incident_time}` : ''}</div></div>}
            </div>
            {td.police_reported && (
              <div style={{marginTop:'0.5rem',padding:'0.5rem',background:'rgba(239,68,68,0.08)',borderRadius:'6px',border:'1px solid rgba(239,68,68,0.2)'}}>
                <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#ef4444',textTransform:'uppercase',marginBottom:'0.25rem'}}>Reported to Police {td.police_reported_via ? `via ${td.police_reported_via}` : ''}</div>
                <div style={{display:'flex',gap:'1rem',fontSize:'0.8125rem',flexWrap:'wrap'}}>
                  {td.police_force && <span><span style={{color:'var(--text-3)'}}>Force:</span> <span style={{fontWeight:600}}>{td.police_force}</span></span>}
                  {td.police_incident_number && <span><span style={{color:'var(--text-3)'}}>Ref:</span> <span style={{fontWeight:600}}>{td.police_incident_number}</span></span>}
                </div>
              </div>
            )}
            {td.police_reported === false && (
              <div style={{marginTop:'0.5rem',padding:'0.375rem 0.5rem',background:'rgba(255,255,255,0.03)',borderRadius:'6px',fontSize:'0.8125rem',color:'var(--text-3)'}}>Not reported to police</div>
            )}
            {td.services_attended?.length > 0 && (
              <div style={{marginTop:'0.5rem',padding:'0.5rem',background:'rgba(59,130,246,0.08)',borderRadius:'6px',border:'1px solid rgba(59,130,246,0.2)'}}>
                <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#60a5fa',textTransform:'uppercase',marginBottom:'0.25rem'}}>Emergency Services Attended</div>
                <div style={{display:'flex',gap:'0.375rem',marginBottom:'0.375rem'}}>
                  {td.services_attended.map(s => <span key={s} style={{padding:'2px 8px',background:'rgba(59,130,246,0.12)',border:'1px solid rgba(59,130,246,0.3)',borderRadius:'4px',fontSize:'0.75rem',fontWeight:600,color:'#60a5fa'}}>{s}</span>)}
                </div>
                <div style={{display:'flex',gap:'1rem',fontSize:'0.8125rem',flexWrap:'wrap'}}>
                  {td.services_time_on && <span><span style={{color:'var(--text-3)'}}>On site:</span> <span style={{fontWeight:600}}>{td.services_time_on}</span></span>}
                  {td.services_time_off && <span><span style={{color:'var(--text-3)'}}>Off site:</span> <span style={{fontWeight:600}}>{td.services_time_off}</span></span>}
                </div>
                {td.police_officer_name && <div style={{fontSize:'0.8125rem',marginTop:'0.25rem'}}><span style={{color:'var(--text-3)'}}>Officers:</span> <span style={{fontWeight:600}}>{td.police_officer_name}</span></div>}
                {td.services_actions && <div style={{fontSize:'0.8125rem',marginTop:'0.25rem'}}><span style={{color:'var(--text-3)'}}>Actions:</span> <span style={{fontWeight:500}}>{td.services_actions}</span></div>}
              </div>
            )}
          </div>
        )}

        {/* Vehicle Report details */}
        {log.log_type === 'VEHICLE_CHECK' && (
          <div style={{marginBottom:'1rem',padding:'0.75rem',background:'var(--surface-2)',borderRadius:'8px',border:'1px solid var(--border)'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Vehicle Details</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem'}}>
              {td.registration && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Registration</div><div style={{fontWeight:700,fontSize:'1rem',color:'var(--text-1)',letterSpacing:'0.05em'}}>{td.registration}</div></div>}
              {td.vehicle_type && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Type</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{td.vehicle_type}</div></div>}
              {td.make_model && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Make / Model</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{td.make_model}</div></div>}
              {td.colour && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Colour</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{td.colour}</div></div>}
              {td.location && <div style={{gridColumn:'1 / -1'}}><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Location</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{td.location}</div></div>}
            </div>
            {td.police_reported && (
              <div style={{marginTop:'0.5rem',padding:'0.5rem',background:'rgba(239,68,68,0.08)',borderRadius:'6px',border:'1px solid rgba(239,68,68,0.2)'}}>
                <div style={{fontSize:'0.6875rem',fontWeight:700,color:'#ef4444',textTransform:'uppercase',marginBottom:'0.25rem'}}>Reported to Police</div>
                <div style={{display:'flex',gap:'1rem',fontSize:'0.8125rem'}}>
                  {td.police_force && <span><span style={{color:'var(--text-3)'}}>Force:</span> <span style={{fontWeight:600}}>{td.police_force}</span></span>}
                  {td.police_incident_number && <span><span style={{color:'var(--text-3)'}}>Ref:</span> <span style={{fontWeight:600}}>{td.police_incident_number}</span></span>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* H&S Report details */}
        {log.log_type === 'HEALTH_SAFETY' && (
          <div style={{marginBottom:'1rem',padding:'0.75rem',background:'var(--surface-2)',borderRadius:'8px',border:'1px solid var(--border)'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Environmental Health & Safety</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem'}}>
              {td.category && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Category</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{td.category}</div></div>}
              {td.severity && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Severity</div><div style={{fontWeight:600,fontSize:'0.875rem',color: td.severity === 'CRITICAL' ? '#ef4444' : td.severity === 'HIGH' ? '#f59e0b' : td.severity === 'MEDIUM' ? '#eab308' : 'var(--text-1)'}}>{td.severity}</div></div>}
              {td.location && <div style={{gridColumn:'1 / -1'}}><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Location</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{td.location}</div></div>}
            </div>
          </div>
        )}

        {/* Description */}
        {log.description && (
          <div style={{marginBottom:'1rem'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.375rem'}}>Description</div>
            <p style={{fontSize:'0.875rem',lineHeight:1.6,color:'var(--text-1)',whiteSpace:'pre-wrap',margin:0}}>{log.description}</p>
          </div>
        )}

        {/* Actions taken */}
        {(td.actions_taken || td.action_taken) && (
          <div style={{marginBottom:'1rem'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.375rem'}}>Actions Taken</div>
            <p style={{fontSize:'0.875rem',lineHeight:1.6,color:'var(--text-1)',whiteSpace:'pre-wrap',margin:0}}>{td.actions_taken || td.action_taken}</p>
          </div>
        )}

        {/* Photos */}
        {media.length > 0 && (
          <div style={{marginBottom:'1rem'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Photos / Video ({media.length})</div>
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
              {media.map((m, i) => {
                const isImg = !m.type || m.type?.startsWith('image') || m.url?.match(/\.(jpg|jpeg|png|gif|webp)/i);
                return (
                  <div key={i} onClick={() => isImg && setLightbox(m.url)}
                    style={{width:80,height:80,borderRadius:'8px',overflow:'hidden',border:'1px solid var(--border)',cursor:isImg?'zoom-in':'default',flexShrink:0}}>
                    {isImg
                      ? <img src={m.url} style={{width:'100%',height:'100%',objectFit:'cover'}} alt={m.name||'photo'} />
                      : <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--surface-2)',fontSize:'0.75rem',color:'var(--text-3)'}}>video</div>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* GPS Location Map */}
        {log.latitude && log.longitude && (
          <div style={{marginBottom:'1rem'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Location</div>
            <div style={{height:'200px',borderRadius:'8px',overflow:'hidden',border:'1px solid var(--border)',position:'relative'}}>
              <iframe
                width="100%" height="100%" frameBorder="0" style={{border:0}}
                src={`https://www.openstreetmap.org/export/embed.html?bbox=${log.longitude-0.003},${log.latitude-0.002},${log.longitude+0.003},${log.latitude+0.002}&layer=mapnik&marker=${log.latitude},${log.longitude}`}
              />
            </div>
            <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginTop:'0.25rem'}}>
              GPS: {log.latitude.toFixed(5)}, {log.longitude.toFixed(5)}
              {log.what3words && <span style={{marginLeft:'0.5rem',color:'#ef4444',fontWeight:600}}>///{log.what3words}</span>}
            </div>
          </div>
        )}

        {/* Extra type_data fields (skip fields already shown above) */}
        {(() => { const shownKeys = ['vehicle_type','registration','make_model','colour','location','police_reported','police_force','police_incident_number','police_reported_via','category','severity','action_taken','actions_taken','sub_type','incident_type','incident_date','incident_time','services_attended','services_time_on','services_time_off','police_officer_name','services_actions','ai_generated','shift_event']; return extraFields.filter(([k]) => !shownKeys.includes(k)); })().length > 0 && (
          <div style={{marginBottom:'1rem'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Additional Details</div>
            <div style={{display:'flex',flexDirection:'column',gap:'0.375rem'}}>
              {(() => { const shownKeys = ['vehicle_type','registration','make_model','colour','location','police_reported','police_force','police_incident_number','police_reported_via','category','severity','action_taken','actions_taken','sub_type','incident_type','incident_date','incident_time','services_attended','services_time_on','services_time_off','police_officer_name','services_actions','ai_generated','shift_event']; return extraFields.filter(([k]) => !shownKeys.includes(k)); })().map(([k, v]) => (
                <div key={k} style={{display:'flex',gap:'0.5rem',fontSize:'0.8125rem'}}>
                  <span style={{color:'var(--text-3)',minWidth:'140px',textTransform:'capitalize'}}>{k.replace(/_/g,' ')}</span>
                  <span style={{color:'var(--text-1)',fontWeight:500}}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
          <img src={lightbox} style={{maxWidth:'100%',maxHeight:'90vh',borderRadius:'8px',objectFit:'contain'}} />
          <button onClick={() => setLightbox(null)} style={{position:'absolute',top:'1rem',right:'1rem',background:'rgba(255,255,255,0.15)',border:'none',color:'#fff',width:36,height:36,borderRadius:'50%',fontSize:'1.25rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
        </div>
      )}
    </div>
  );
}

// ── Patrol Detail Modal ──────────────────────────────────────────────────────
function PatrolDetailModal({ log, onClose }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionError, setSessionError] = useState(null);
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  useEffect(() => {
    if (!log.type_data?.patrol_session_id) { setLoading(false); return; }
    api.patrols.getSession(log.type_data.patrol_session_id)
      .then(res => { if (res?.data) setSession(res.data); else setSessionError('Session data not found'); })
      .catch(err => setSessionError(err.message || 'Failed to load patrol session'))
      .finally(() => setLoading(false));
  }, []);

  // Render map when session loads
  useEffect(() => {
    if (!session || !mapRef.current || mapInstance.current) return;
    function renderMap() {
      if (!window.L || !mapRef.current) return;
      const L = window.L;
      const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false });
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { maxZoom: 19 }).addTo(map);
      const allPoints = [];

      // GPS trail
      if (session.gps_trail?.length) {
        const trail = session.gps_trail.filter(p => p.lat && p.lng).map(p => [p.lat, p.lng]);
        if (trail.length > 0) {
          L.polyline(trail, { color: '#3b82f6', weight: 3, opacity: 0.8 }).addTo(map);
          // Start marker (green)
          L.circleMarker(trail[0], { radius: 8, fillColor: '#22c55e', fillOpacity: 1, color: '#fff', weight: 2 }).addTo(map).bindPopup('Start');
          // End marker (red)
          L.circleMarker(trail[trail.length - 1], { radius: 8, fillColor: '#ef4444', fillOpacity: 1, color: '#fff', weight: 2 }).addTo(map).bindPopup('End');
          allPoints.push(...trail);
        }
      }

      // Checkpoint markers
      if (session.checkpoints_completed?.length) {
        session.checkpoints_completed.forEach((cp, i) => {
          if (!cp.lat || !cp.lng) return;
          const icon = L.divIcon({ html: `<div style="width:22px;height:22px;background:#a78bfa;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:#0b1222">${i+1}</div>`, iconSize:[22,22], iconAnchor:[11,11] });
          L.marker([cp.lat, cp.lng], { icon }).addTo(map).bindPopup(cp.name || `Checkpoint ${i+1}`);
          allPoints.push([cp.lat, cp.lng]);
        });
      }

      if (allPoints.length > 0) map.fitBounds(allPoints, { padding: [30, 30] });
      else map.setView([52.48, -1.89], 14);
      mapInstance.current = map;
    }

    if (window.L) { renderMap(); }
    else {
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link'); link.id = 'leaflet-css'; link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link);
      }
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script'); script.id = 'leaflet-js';
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = renderMap; document.head.appendChild(script);
      } else { setTimeout(renderMap, 200); }
    }
    return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
  }, [session]);

  // Calculate distance from GPS trail
  function calcDistance(trail) {
    if (!trail?.length || trail.length < 2) return null;
    let total = 0;
    for (let i = 1; i < trail.length; i++) {
      const R = 6371000;
      const dLat = (trail[i].lat - trail[i-1].lat) * Math.PI / 180;
      const dLng = (trail[i].lng - trail[i-1].lng) * Math.PI / 180;
      const a = Math.sin(dLat/2)**2 + Math.cos(trail[i-1].lat*Math.PI/180) * Math.cos(trail[i].lat*Math.PI/180) * Math.sin(dLng/2)**2;
      total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    }
    return total;
  }

  const td = log.type_data || {};
  const duration = td.duration_minutes;
  const distMetres = session?.gps_trail ? calcDistance(session.gps_trail.filter(p => p.lat && p.lng)) : null;
  const checkpoints = session?.checkpoints_completed || td.checkpoints_completed || [];
  const fmtTime = t => t ? new Date(t).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit', timeZone:'Europe/London' }) : '—';
  const fmtDate = t => t ? new Date(t).toLocaleDateString('en-GB', { weekday:'short', day:'2-digit', month:'short', year:'numeric', timeZone:'Europe/London' }) : '—';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:'700px',maxHeight:'90vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Patrol Detail</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Header */}
        <div style={{display:'flex',gap:'1rem',flexWrap:'wrap',marginBottom:'1rem'}}>
          <div>
            <div style={{fontSize:'0.75rem',color:'var(--text-3)'}}>Officer</div>
            <div style={{fontWeight:600}}>{log.officer ? `${log.officer.first_name} ${log.officer.last_name}` : '—'}</div>
          </div>
          <div>
            <div style={{fontSize:'0.75rem',color:'var(--text-3)'}}>Site</div>
            <div style={{fontWeight:600}}>{log.site?.name || '—'}</div>
          </div>
          <div>
            <div style={{fontSize:'0.75rem',color:'var(--text-3)'}}>Date</div>
            <div>{fmtDate(td.started_at || log.occurred_at)}</div>
          </div>
          <div>
            <div style={{fontSize:'0.75rem',color:'var(--text-3)'}}>Time</div>
            <div>{fmtTime(td.started_at || log.occurred_at)}{td.ended_at ? ` → ${fmtTime(td.ended_at)}` : ''}</div>
          </div>
          {duration != null && (
          <div>
            <div style={{fontSize:'0.75rem',color:'var(--text-3)'}}>Duration</div>
            <div style={{fontWeight:600}}>{duration >= 60 ? `${Math.floor(duration/60)}h ${duration%60}m` : `${duration}m`}</div>
          </div>
          )}
        </div>

        {/* Stats */}
        <div style={{display:'flex',gap:'0.75rem',marginBottom:'1rem'}}>
          <div style={{flex:1,padding:'0.625rem',background:'var(--surface-2)',borderRadius:'6px',textAlign:'center'}}>
            <div style={{fontSize:'1.25rem',fontWeight:700}}>{checkpoints.length}</div>
            <div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Checkpoints</div>
          </div>
          {distMetres != null && (
            <div style={{flex:1,padding:'0.625rem',background:'var(--surface-2)',borderRadius:'6px',textAlign:'center'}}>
              <div style={{fontSize:'1.25rem',fontWeight:700}}>{distMetres >= 1000 ? `${(distMetres/1000).toFixed(1)}km` : `${Math.round(distMetres)}m`}</div>
              <div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Distance</div>
            </div>
          )}
          {session?.gps_trail?.length > 0 && (
            <div style={{flex:1,padding:'0.625rem',background:'var(--surface-2)',borderRadius:'6px',textAlign:'center'}}>
              <div style={{fontSize:'1.25rem',fontWeight:700}}>{session.gps_trail.length}</div>
              <div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>GPS Points</div>
            </div>
          )}
        </div>

        {/* Map */}
        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'2rem'}}><div className="spinner" /></div>
        ) : sessionError ? (
          <div style={{padding:'1rem',background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:'8px',color:'#ef4444',fontSize:'0.875rem',marginBottom:'1rem'}}>Could not load patrol map: {sessionError}</div>
        ) : !session ? (
          <div style={{padding:'1rem',background:'var(--surface-2)',borderRadius:'8px',color:'var(--text-3)',fontSize:'0.875rem',marginBottom:'1rem'}}>No session data found for this patrol.</div>
        ) : (
          <div ref={mapRef} style={{width:'100%',height:'350px',borderRadius:'8px',border:'1px solid var(--border)',marginBottom:'1rem'}} />
        )}

        {/* Checkpoints */}
        {checkpoints.length > 0 && (
          <div>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Checkpoints Completed</div>
            <div style={{display:'flex',flexDirection:'column',gap:'0.375rem'}}>
              {checkpoints.map((cp, i) => (
                <div key={i} style={{display:'flex',alignItems:'center',gap:'0.625rem',padding:'0.5rem 0.625rem',background:'var(--surface-2)',borderRadius:'6px'}}>
                  <div style={{width:22,height:22,borderRadius:'50%',background:'#a78bfa',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',fontWeight:700,color:'#0b1222',flexShrink:0}}>{i+1}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:'0.8125rem',fontWeight:500}}>{typeof cp === 'object' ? cp.name || `Checkpoint ${i+1}` : cp}</div>
                  </div>
                  {typeof cp === 'object' && cp.timestamp && (
                    <div style={{fontSize:'0.75rem',color:'var(--text-3)',flexShrink:0}}>{fmtTime(cp.timestamp)}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Media from patrol reports/occurrences */}
        {(() => {
          const allMedia = [
            ...(td.media || []),
            ...(td.photo_url ? [{ url: td.photo_url, type: 'image/jpeg', name: 'checkpoint-photo' }] : []),
            ...(session?.checkpoints_completed || []).flatMap(cp => [
              ...(cp.media || []),
              ...(cp.photo_url ? [{ url: cp.photo_url, type: 'image/jpeg', name: cp.name || 'checkpoint-photo' }] : []),
            ]),
          ];
          if (!allMedia.length) return null;
          return (
            <div style={{marginTop:'1rem'}}>
              <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Photos / Video ({allMedia.length})</div>
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                {allMedia.map((m, i) => (
                  <div key={i} style={{width:80,height:80,borderRadius:'8px',overflow:'hidden',border:'1px solid var(--border)',flexShrink:0,cursor:'pointer'}}
                    onClick={() => window.open(m.url, '_blank')}>
                    {m.type?.startsWith('image')
                      ? <img src={m.url} style={{width:'100%',height:'100%',objectFit:'cover'}} alt={m.name||'photo'} />
                      : <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',background:'var(--surface-2)',fontSize:'0.75rem',color:'var(--text-3)'}}>video</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// Task Assignment Screen
function TaskAssignment({ user }) {
  const [tasks, setTasks] = useState([]);
  const [officers, setOfficers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [tab, setTab] = useState('PENDING');

  async function load() {
    try {
      const [tasksRes, officersRes, sitesRes] = await Promise.all([
        api.tasks.list(),
        api.users.list(),
        api.sites.list(),
      ]);
      setTasks(tasksRes.data || []);
      setOfficers(officersRes.data?.filter(u => u.role === 'OFFICER') || []);
      setSites(sitesRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filtered = tasks.filter(t => tab === 'ALL' || t.status === tab || (!t.status && tab === 'PENDING'));
  const counts = {
    PENDING: tasks.filter(t => !t.status || t.status === 'PENDING').length,
    IN_PROGRESS: tasks.filter(t => t.status === 'IN_PROGRESS').length,
    COMPLETE: tasks.filter(t => t.status === 'COMPLETED').length,
  };

  const [editTask, setEditTask] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  async function updateStatus(taskId, status) {
    await api.tasks.update(taskId, { status });
    load();
  }

  async function deleteTask(id) {
    try { await api.tasks.delete(id); setDeleteConfirmId(null); load(); }
    catch (e) { alert(e.message); }
  }

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Assignments</div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>
          <PlusIcon style={{width:'0.875rem',height:'0.875rem'}} /> Assign Task
        </button>
      </div>
      <div className="page-content">
        <div className="tabs">
          {[['PENDING','Pending'],['IN_PROGRESS','In Progress'],['COMPLETED','Complete'],['ALL','All']].map(([val,label]) => (
            <button key={val} className={`tab${tab===val?' active':''}`} onClick={() => setTab(val)}>
              {label} {val !== 'ALL' && <span style={{fontSize:'0.75rem',color:'inherit',opacity:0.7}}>({counts[val]||0})</span>}
            </button>
          ))}
        </div>
        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><p>No tasks</p></div>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Task</th><th>Urgency</th><th>Assigned To</th><th>Site</th><th>Due</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(task => (
                <tr key={task.id}>
                  <td>
                    <div style={{fontWeight:500}}>{task.title}</div>
                    {task.description && <div style={{fontSize:'0.75rem',color:'var(--text-2)'}}>{task.description}</div>}
                  </td>
                  <td><span className="badge" style={{background:task.urgency==='now'?'rgba(239,68,68,0.15)':task.urgency==='today'?'rgba(245,158,11,0.15)':'rgba(59,130,246,0.1)',color:task.urgency==='now'?'#ef4444':task.urgency==='today'?'#f59e0b':'#60a5fa',fontWeight:700,fontSize:'0.6875rem'}}>{task.urgency==='now'?'NOW':task.urgency==='today'?'TODAY':'ROUTINE'}</span></td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>
                    {task.assigned_to_user ? `${task.assigned_to_user.first_name} ${task.assigned_to_user.last_name}` : '—'}
                  </td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{task.site?.name || '—'}</td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>
                    {task.due_date ? new Date(task.due_date).toLocaleDateString('en-GB') : '—'}
                  </td>
                  <td>
                    <span className={`badge ${task.status==='COMPLETED'?'badge-success':task.status==='IN_PROGRESS'?'badge-blue':'badge-neutral'}`}>
                      {task.status || 'Pending'}
                    </span>
                  </td>
                  <td style={{textAlign:'right',display:'flex',gap:'0.375rem',justifyContent:'flex-end'}}>
                    {task.status !== 'COMPLETED' && (
                      <button className="btn btn-ghost btn-sm" onClick={() => updateStatus(task.id, task.status === 'IN_PROGRESS' ? 'COMPLETED' : 'IN_PROGRESS')}>
                        {task.status === 'IN_PROGRESS' ? 'Complete' : 'Start'}
                      </button>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditTask(task)}>Edit</button>
                    {deleteConfirmId === task.id ? (
                      <>
                        <button className="btn btn-sm" style={{background:'var(--danger)',color:'#fff',border:'none'}} onClick={() => deleteTask(task.id)}>Yes</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDeleteConfirmId(null)}>No</button>
                      </>
                    ) : (
                      <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={() => setDeleteConfirmId(task.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showForm && (
        <TaskCreateForm
          officers={officers}
          sites={sites}
          onClose={() => setShowForm(false)}
          onSuccess={() => { setShowForm(false); load(); }}
        />
      )}
      {editTask && (
        <TaskEditModal
          task={editTask}
          officers={officers}
          sites={sites}
          onClose={() => setEditTask(null)}
          onSaved={() => { setEditTask(null); load(); }}
        />
      )}
    </div>
  );
}


function TaskEditModal({ task, officers, sites, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: task.title || '', description: task.description || '',
    assigned_to: task.assigned_to || '', site_id: task.site_id || '',
    due_date: task.due_date ? task.due_date.split('T')[0] : '', urgency: task.urgency || 'normal',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    try {
      await api.tasks.update(task.id, {
        title: form.title, description: form.description || null,
        assigned_to: form.assigned_to || null, site_id: form.site_id || null,
        due_date: form.due_date || null, urgency: form.urgency || 'normal',
      });
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Edit Assignment</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        <div className="field"><label className="label">Title</label><input className="input" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} /></div>
        <div className="field"><label className="label">Description</label><textarea className="input" rows={3} value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} /></div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field"><label className="label">Assign To</label><select className="input" value={form.assigned_to} onChange={e => setForm(f=>({...f,assigned_to:e.target.value}))}><option value="">Unassigned</option>{officers.map(o => <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>)}</select></div>
          <div className="field"><label className="label">Site</label><select className="input" value={form.site_id} onChange={e => setForm(f=>({...f,site_id:e.target.value}))}><option value="">No site</option>{sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
        </div>
        <div className="field">
          <label className="label">Urgency</label>
          <div style={{display:'flex',gap:'6px'}}>
            {[{k:'now',l:'NOW',c:'#ef4444',bg:'rgba(239,68,68,0.12)',b:'rgba(239,68,68,0.4)'},{k:'today',l:'TODAY',c:'#f59e0b',bg:'rgba(245,158,11,0.1)',b:'rgba(245,158,11,0.35)'},{k:'normal',l:'ROUTINE',c:'#60a5fa',bg:'rgba(59,130,246,0.1)',b:'rgba(59,130,246,0.3)'}].map(u => (
              <button key={u.k} type="button" onClick={() => setForm(f=>({...f,urgency:u.k}))}
                style={{flex:1,padding:'10px',background:form.urgency===u.k?u.bg:'transparent',border:`1.5px solid ${form.urgency===u.k?u.b:'var(--border)'}`,borderRadius:'8px',color:form.urgency===u.k?u.c:'var(--text-3)',fontSize:'0.8125rem',fontWeight:700,cursor:'pointer'}}>
                {u.l}
              </button>
            ))}
          </div>
        </div>
        <div className="field"><label className="label">Due Date</label><input type="date" className="input" value={form.due_date} onChange={e => setForm(f=>({...f,due_date:e.target.value}))} /></div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

function TaskCreateForm({ officers, sites, onClose, onSuccess }) {
  const [form, setForm] = useState({ title: '', description: '', assigned_to: '', site_id: '', due_date: '', urgency: 'normal' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!form.title.trim()) { setError('Title is required'); return; }
    try {
      setSaving(true);
      await api.tasks.create({
        title: form.title,
        description: form.description || null,
        assigned_to: form.assigned_to || null,
        site_id: form.site_id || null,
        due_date: form.due_date || null,
        urgency: form.urgency || 'normal',
      });
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Assign Task</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        <div className="field">
          <label className="label">Task Title</label>
          <input className="input" value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} placeholder="What needs to be done?" />
        </div>
        <div className="field">
          <label className="label">Description</label>
          <textarea className="input" rows={3} value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} placeholder="Additional details..." />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field">
            <label className="label">Assign To</label>
            <select className="input" value={form.assigned_to} onChange={e => setForm(f=>({...f,assigned_to:e.target.value}))}>
              <option value="">Unassigned</option>
              {officers.map(o => <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>)}
            </select>
          </div>
          <div className="field">
            <label className="label">Site</label>
            <select className="input" value={form.site_id} onChange={e => setForm(f=>({...f,site_id:e.target.value}))}>
              <option value="">No site</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="field">
          <label className="label">Urgency</label>
          <div style={{display:'flex',gap:'6px'}}>
            {[{k:'now',l:'NOW',c:'#ef4444',bg:'rgba(239,68,68,0.12)',b:'rgba(239,68,68,0.4)'},{k:'today',l:'TODAY',c:'#f59e0b',bg:'rgba(245,158,11,0.1)',b:'rgba(245,158,11,0.35)'},{k:'normal',l:'ROUTINE',c:'#60a5fa',bg:'rgba(59,130,246,0.1)',b:'rgba(59,130,246,0.3)'}].map(u => (
              <button key={u.k} type="button" onClick={() => setForm(f=>({...f,urgency:u.k}))}
                style={{flex:1,padding:'10px',background:form.urgency===u.k?u.bg:'transparent',border:`1.5px solid ${form.urgency===u.k?u.b:'var(--border)'}`,borderRadius:'8px',color:form.urgency===u.k?u.c:'var(--text-3)',fontSize:'0.8125rem',fontWeight:700,cursor:'pointer'}}>
                {u.l}
              </button>
            ))}
          </div>
        </div>
        <div className="field">
          <label className="label">Due Date</label>
          <input type="date" className="input" value={form.due_date} onChange={e => setForm(f=>({...f,due_date:e.target.value}))} />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Assign Task'}</button>
        </div>
      </div>
    </div>
  );
}



function SiteDetail({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [assignedOfficers, setAssignedOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    if (!id) return;
    async function fetchSite() {
      try {
        setLoading(true);
        const [siteRes, logsRes, shiftsRes, usersRes] = await Promise.all([
          api.sites.get(id),
          api.logs.list({ site_id: id, limit: 10 }),
          api.shifts.list({ site_id: id, limit: 50 }),
          api.users.list(),
        ]);
        setSite(siteRes.data);
        setRecentLogs(logsRes.data || []);
        setShifts(shiftsRes.data || []);
        // Load site assignments for each officer to find who's assigned here
        const officers = (usersRes.data || []).filter(u => u.role === 'OFFICER');
        const withSites = await Promise.all(officers.map(async o => {
          try {
            const res = await api.officerSites.list(o.id);
            const siteIds = (res.data || []).map(s => s.id);
            return siteIds.includes(id) ? o : null;
          } catch { return null; }
        }));
        setAssignedOfficers(withSites.filter(Boolean));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchSite();
  }, [id]);

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'16rem'}}>
      <div className="spinner" />
    </div>
  );

  if (error || !site) return (
    <div className="page-content">
      <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error || 'Site not found'}</div>
      <button onClick={() => navigate('/sites')} className="btn btn-secondary">← Back to Sites</button>
    </div>
  );

  return (
    <div>
      <div className="topbar">
        <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
          <button onClick={() => navigate('/sites')} style={{background:'none',border:'none',color:'var(--text-2)',cursor:'pointer',padding:0,fontSize:'1.125rem'}}>←</button>
          <div>
            <div className="topbar-title">{site.name}</div>
            {site.client && <div className="topbar-sub">{site.client.client_company_name}</div>}
          </div>
        </div>
        <span className={`badge ${site.active !== false ? 'badge-success' : 'badge-neutral'}`}>
          {site.active !== false ? 'Active' : 'Inactive'}
        </span>
      </div>
      {/* Tab bar */}
      <div style={{display:'flex',gap:0,borderBottom:'1px solid var(--border)',padding:'0 1.5rem',background:'var(--surface)'}}>
        {[{key:'info',label:'Site Info'},{key:'ai',label:'Assignment Instructions'},{key:'logs',label:'Recent Logs'},{key:'roster',label:'Roster'},{key:'officers',label:'Officers'},{key:'visitors',label:'Visitors'},{key:'documents',label:'Inspections'},{key:'risks',label:'Risk Assessments'},{key:'codes',label:'Codes'},{key:'playbook',label:'Virtual Supervisor'}].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{padding:'0.75rem 1rem',background:'none',border:'none',borderBottom:`2px solid ${activeTab===t.key?'var(--blue)':'transparent'}`,color:activeTab===t.key?'var(--blue)':'var(--text-2)',fontSize:'0.875rem',fontWeight:600,cursor:'pointer',marginBottom:'-1px',whiteSpace:'nowrap'}}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="page-content">
        {activeTab === 'ai' && <SiteAIEditor siteId={id} siteName={site.name} />}
        {activeTab === 'playbook' && <SitePlaybook siteId={id} />}
        {activeTab === 'risks' && <RiskAssessmentList siteId={id} siteName={site.name} user={user} />}
        {activeTab === 'logs' && (
          <div className="card">
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.875rem'}}>
              <div className="section-title">Recent Logs</div>
              <span style={{fontSize:'0.75rem',color:'var(--text-3)'}}>Last 10</span>
            </div>
            {recentLogs.length === 0 ? (
              <div className="empty-state"><p>No logs recorded for this site</p></div>
            ) : (
              recentLogs.map(log => <ManagerLogCard key={log.id} log={log} />)
            )}
          </div>
        )}
        {activeTab === 'roster' && (
          <RosterCalendar siteId={id} user={user} />
        )}
        {activeTab === 'officers' && (
          <div className="card">
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.875rem'}}>
              <div className="section-title">Assigned Officers</div>
              <span style={{fontSize:'0.75rem',color:'var(--text-3)'}}>{assignedOfficers.length} officer{assignedOfficers.length!==1?'s':''}</span>
            </div>
            {assignedOfficers.length === 0 ? (
              <div className="empty-state"><p>No officers assigned to this site</p></div>
            ) : (
              <table className="table">
                <thead>
                  <tr><th>Name</th><th>SIA Licence</th><th>SIA Expiry</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {assignedOfficers.map(o => {
                    const now = new Date();
                    const exp1 = o.sia_expiry_date ? new Date(o.sia_expiry_date) : null;
                    const exp2 = o.sia_expiry_date_2 ? new Date(o.sia_expiry_date_2) : null;
                    const days1 = exp1 ? (exp1 - now) / 86400000 : null;
                    const days2 = exp2 ? (exp2 - now) / 86400000 : null;
                    const anyExpired = (days1 !== null && days1 < 0) || (days2 !== null && days2 < 0);
                    const anyExpiring = (days1 !== null && days1 >= 0 && days1 < 90) || (days2 !== null && days2 >= 0 && days2 < 90);
                    const siaColor = anyExpired ? '#ef4444' : anyExpiring ? '#f59e0b' : (days1 !== null || days2 !== null) ? '#22c55e' : 'var(--text-3)';
                    const siaLabel = anyExpired ? 'Expired' : anyExpiring ? 'Expiring' : (days1 !== null || days2 !== null) ? 'Valid' : '—';
                    return (
                      <tr key={o.id}>
                        <td style={{fontWeight:500}}>{o.first_name} {o.last_name}</td>
                        <td style={{fontFamily:'monospace',fontSize:'0.8125rem',color:'var(--text-2)'}}>{o.sia_licence_number||'—'}</td>
                        <td style={{fontSize:'0.8125rem'}}>
                          {exp1 ? new Date(o.sia_expiry_date).toLocaleDateString('en-GB') : '—'}
                        </td>
                        <td>
                          <span style={{display:'inline-flex',alignItems:'center',gap:'0.375rem',fontSize:'0.8125rem',color:siaColor,fontWeight:600}}>
                            <span style={{width:8,height:8,borderRadius:'50%',background:siaColor,display:'inline-block'}} />
                            {siaLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
        {activeTab === 'visitors' && <SiteVisitorsTab siteId={id} />}
        {activeTab === 'documents' && <SiteDocumentsTab siteId={id} />}
        {activeTab === 'codes' && <SiteCodesTab siteId={id} />}
        {activeTab === 'info' && <div>
        <div className="card" style={{marginBottom:'1rem'}}>
          <div className="section-title" style={{marginBottom:'0.875rem'}}>Site Details</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
            <InfoField label="Name" value={site.name} />
            <InfoField label="Status" value={site.active !== false ? 'Active' : 'Inactive'} />
            <InfoField label="Address" value={site.address} span />
            <InfoField label="City" value={site.city} />
            <InfoField label="Postcode" value={site.postcode} />
            <InfoField label="Notes" value={site.notes} span />
          </div>
        </div>
        <div className="card" style={{marginBottom:'1rem'}}>
          <div className="section-title" style={{marginBottom:'0.875rem'}}>Site Contact</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
            <InfoField label="Contact Name" value={site.contact_name} />
            <InfoField label="Phone" value={site.contact_phone} />
            <InfoField label="Email" value={site.contact_email} span />
          </div>
        </div>
        <div className="card" style={{marginBottom:'1rem'}}>
          <div className="section-title" style={{marginBottom:'0.875rem'}}>Escalation Contacts</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
            <InfoField label="Escalation 1 Name" value={site.escalation_contact_1_name} />
            <InfoField label="Escalation 1 Mobile" value={site.escalation_contact_1_mobile} />
            <InfoField label="Escalation 2 Name" value={site.escalation_contact_2_name} />
            <InfoField label="Escalation 2 Mobile" value={site.escalation_contact_2_mobile} />
          </div>
        </div>
        <div className="card" style={{marginBottom:'1rem'}}>
          <div className="section-title" style={{marginBottom:'0.875rem'}}>Client</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
            <InfoField label="Client Name" value={site.client_name} />
            <InfoField label="Contract Start Date" value={site.contract_start_date ? new Date(site.contract_start_date).toLocaleDateString('en-GB') : null} />
            <InfoField label="Client Company Address" value={site.client_company_address} span />
            <InfoField label="Contact Name" value={site.client_contact_name} />
            <InfoField label="Contact Email" value={site.client_contact_email} />
            <InfoField label="Contact Phone" value={site.client_contact_phone} />
          </div>
        </div>
        <div className="card">
          <div className="section-title" style={{marginBottom:'0.875rem'}}>Geofence</div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0.75rem'}}>
            <InfoField label="Latitude" value={site.geofence_lat} />
            <InfoField label="Longitude" value={site.geofence_lng} />
            <InfoField label="Radius (m)" value={site.geofence_radius} />
          </div>
        </div>
        </div>}
      </div>
    </div>
  );
}

// ── TEAM MANAGEMENT ────────────────────────────────────────────────────────────
function SiteAssignModal({ officer, onClose }) {
  const [sites, setSites] = useState([]);
  const [assigned, setAssigned] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const [allSites, officerSites] = await Promise.all([
          api.sites.list(),
          api.officerSites.list(officer.id),
        ]);
        setSites(allSites.data || []);
        setAssigned(new Set((officerSites.data || []).map(s => s.id)));
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    }
    load();
  }, [officer.id]);

  function toggle(siteId) {
    setAssigned(prev => {
      const next = new Set(prev);
      next.has(siteId) ? next.delete(siteId) : next.add(siteId);
      return next;
    });
  }

  async function save() {
    try {
      setSaving(true);
      await api.officerSites.update(officer.id, [...assigned]);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Assign Sites — {officer.first_name} {officer.last_name}</div>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'2rem'}}><div className="spinner" /></div>
        ) : sites.length === 0 ? (
          <div className="empty-state"><p>No sites available</p></div>
        ) : (
          <div style={{display:'flex',flexDirection:'column',gap:'0.5rem',maxHeight:'320px',overflowY:'auto'}}>
            {sites.map(site => (
              <label key={site.id} style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.625rem',border:'1px solid var(--border)',borderRadius:'var(--radius)',cursor:'pointer',background:assigned.has(site.id)?'var(--blue-light)':'var(--surface)'}}>
                <input
                  type="checkbox"
                  checked={assigned.has(site.id)}
                  onChange={() => toggle(site.id)}
                  style={{width:'1rem',height:'1rem',accentColor:'var(--blue)'}}
                />
                <div>
                  <div style={{fontSize:'0.875rem',fontWeight:500}}>{site.name}</div>
                  {site.address && <div style={{fontSize:'0.75rem',color:'var(--text-2)'}}>{site.address}</div>}
                </div>
              </label>
            ))}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving||loading}>
            {saving ? 'Saving...' : 'Save Assignments'}
          </button>
        </div>
      </div>
    </div>
  );
}


function TeamManagement({ user }) {
  const [officers, setOfficers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [siteAssignOfficer, setSiteAssignOfficer] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [hrOfficer, setHrOfficer] = useState(null);

  async function load() {
    try {
      const res = await api.users.list();
      const sorted = (res.data || []).sort((a, b) =>
        (a.last_name || '').localeCompare(b.last_name || '') || (a.first_name || '').localeCompare(b.first_name || '')
      );
      setOfficers(sorted);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const roleLabels = { OFFICER:'Officer', OPS_MANAGER:'Ops Manager', FD:'Field Director', COMPANY:'Admin', SUPER_ADMIN:'Super Admin' };
  const roleBadge  = { OFFICER:'badge-neutral', OPS_MANAGER:'badge-blue', FD:'badge-navy', COMPANY:'badge-navy', SUPER_ADMIN:'badge-danger' };
  const isSiaExpired      = d => d && new Date(d) < new Date();
  const isSiaExpiringSoon = d => { if (!d) return false; const days=(new Date(d)-new Date())/86400000; return days>0&&days<90; };

  const filtered = officers.filter(o => {
    if (statusFilter === 'active' && o.active === false) return false;
    if (statusFilter === 'inactive' && o.active !== false) return false;
    if (search) {
      const q = search.toLowerCase();
      return (o.first_name || '').toLowerCase().includes(q) ||
             (o.last_name || '').toLowerCase().includes(q) ||
             (o.email || '').toLowerCase().includes(q) ||
             (o.sia_licence_number || '').toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Team</div>
        <button className="btn btn-primary btn-sm" onClick={() => { setEditUser(null); setShowForm(true); }}>
          <PlusIcon style={{width:'0.875rem',height:'0.875rem'}} /> Add Officer
        </button>
      </div>
      <div className="page-content">
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        {successMsg && <div className="alert alert-success" style={{marginBottom:'1rem'}}>{successMsg}</div>}

        {/* Search + filter bar */}
        {!loading && (
          <div style={{display:'flex',alignItems:'center',gap:'0.75rem',marginBottom:'1rem',flexWrap:'wrap'}}>
            <input className="input" style={{width:'220px'}} placeholder="Search name, email, SIA..." value={search} onChange={e => setSearch(e.target.value)} />
            <div style={{display:'flex',gap:'2px',background:'var(--surface-2)',borderRadius:'6px',padding:'2px'}}>
              {[['all','All'],['active','Active'],['inactive','Inactive']].map(([k,l]) => (
                <button key={k} onClick={() => setStatusFilter(k)}
                  style={{padding:'0.375rem 0.75rem',borderRadius:'4px',border:'none',cursor:'pointer',fontSize:'0.8125rem',fontWeight:600,
                    background: statusFilter===k ? 'var(--blue)' : 'transparent',
                    color: statusFilter===k ? '#fff' : 'var(--text-2)'}}>
                  {l}
                </button>
              ))}
            </div>
            <span style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>{filtered.length} officer{filtered.length!==1?'s':''}</span>
          </div>
        )}

        <IDCardsSummaryCard />

        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><p>{search || statusFilter !== 'all' ? 'No officers match your search' : 'No team members yet'}</p></div>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Phone</th><th>Role</th><th>SIA Type</th><th>SIA No.</th><th>SIA Expiry</th><th>2nd SIA</th><th>BS7858</th><th>Status</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.id}>
                  <td style={{fontWeight:500}}>{o.first_name} {o.last_name}{o.employee_number && <span style={{color:'var(--text-3)',fontWeight:400,fontSize:'0.75rem',marginLeft:'0.375rem'}}>({o.employee_number})</span>}<div style={{fontSize:'0.75rem',color:'var(--text-3)'}}>{o.email}</div></td>
                  <td style={{fontSize:'0.8125rem',color:'var(--text-2)'}}>{o.phone||'—'}</td>
                  <td><span className={`badge ${roleBadge[o.role]||'badge-neutral'}`}>{roleLabels[o.role]||o.role}</span></td>
                  <td style={{fontSize:'0.8125rem',color:'var(--text-2)'}}>{o.sia_licence_type||'—'}</td>
                  <td style={{fontFamily:'monospace',fontSize:'0.8125rem',color:'var(--text-2)'}}>{o.sia_licence_number||'—'}</td>
                  <td style={{fontSize:'0.8125rem'}}>
                    {o.sia_expiry_date ? (
                      <span style={{color:isSiaExpired(o.sia_expiry_date)?'var(--danger)':isSiaExpiringSoon(o.sia_expiry_date)?'var(--warning)':'var(--text-2)'}}>
                        {new Date(o.sia_expiry_date).toLocaleDateString('en-GB')}
                        {isSiaExpired(o.sia_expiry_date)&&' (Expired)'}
                        {isSiaExpiringSoon(o.sia_expiry_date)&&' (Soon)'}
                      </span>
                    ):'—'}
                  </td>
                  <td style={{fontSize:'0.75rem',color:'var(--text-2)'}}>
                    {o.sia_licence_type_2 ? <div>{o.sia_licence_type_2}<div style={{fontFamily:'monospace',fontSize:'0.6875rem'}}>{o.sia_licence_number_2||''}</div>{o.sia_expiry_date_2 && <div style={{color:isSiaExpired(o.sia_expiry_date_2)?'var(--danger)':isSiaExpiringSoon(o.sia_expiry_date_2)?'var(--warning)':'var(--text-3)'}}>{new Date(o.sia_expiry_date_2).toLocaleDateString('en-GB')}</div>}</div> : '—'}
                  </td>
                  <td style={{fontSize:'0.75rem',color:'var(--text-2)'}}>
                    {o.bs7858_expiry_date ? <span style={{color:isSiaExpired(o.bs7858_expiry_date)?'var(--danger)':isSiaExpiringSoon(o.bs7858_expiry_date)?'var(--warning)':'var(--text-2)'}}>{new Date(o.bs7858_expiry_date).toLocaleDateString('en-GB')}</span> : '—'}
                  </td>
                  <td><span className={`badge ${o.active!==false?'badge-success':'badge-neutral'}`}>{o.active!==false?'Active':'Inactive'}</span></td>
                  <td style={{textAlign:'right',display:'flex',gap:'0.375rem',justifyContent:'flex-end',flexWrap:'wrap'}}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setSiteAssignOfficer(o)}>Sites</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => setHrOfficer(o)}>HR</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setEditUser(o); setShowForm(true); }}>Edit</button>
                    {!o.clerk_id && (
                      <button className="btn btn-ghost btn-sm" disabled={resendingId===o.id} onClick={async () => {
                        setResendingId(o.id);
                        try {
                          const r = await api.invite.resend(o.id);
                          setSuccessMsg(r.message || `Invite resent to ${o.email}`);
                          setTimeout(() => setSuccessMsg(null), 8000);
                        } catch(err) {
                          alert('Could not resend: ' + err.message);
                        } finally {
                          setResendingId(null);
                        }
                      }}>{resendingId===o.id ? '...' : 'Resend'}</button>
                    )}
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={async () => {
                      if (!window.confirm(`Delete ${o.first_name} ${o.last_name}? This cannot be undone.`)) return;
                      try { await api.users.delete(o.id); load(); }
                      catch(e) { alert('Could not delete: ' + e.message); }
                    }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showForm && (
        <UserFormModal
          user={editUser}
          onClose={() => setShowForm(false)}
          onSaved={(msg) => { setShowForm(false); load(); if (msg) { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(null), 5000); } }}
        />
      )}
      {siteAssignOfficer && (
        <SiteAssignModal
          officer={siteAssignOfficer}
          onClose={() => setSiteAssignOfficer(null)}
        />
      )}
      {hrOfficer && (
        <HRDetailModal
          officer={hrOfficer}
          onClose={() => setHrOfficer(null)}
        />
      )}
    </div>
  );
}

function HRDetailModal({ officer, onClose }) {
  const [hr, setHr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docUrls, setDocUrls] = useState({});

  useEffect(() => {
    api.hr.getForUser(officer.id)
      .then(res => setHr(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [officer.id]);

  async function viewDoc(docType) {
    if (docUrls[docType]) { window.open(docUrls[docType], '_blank'); return; }
    try {
      const res = await api.hr.getDocUrl(docType, officer.id);
      setDocUrls(prev => ({ ...prev, [docType]: res.url }));
      window.open(res.url, '_blank');
    } catch { alert('Could not load document'); }
  }

  const Row = ({ label, value }) => value ? (
    <div style={{display:'flex',justifyContent:'space-between',padding:'0.5rem 0',borderBottom:'1px solid var(--border)'}}>
      <span style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>{label}</span>
      <span style={{fontSize:'0.8125rem',fontWeight:600,color:'var(--text-1)'}}>{value}</span>
    </div>
  ) : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:'560px',maxHeight:'90vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">HR Record — {officer.first_name} {officer.last_name}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
        ) : !hr ? (
          <div style={{padding:'2rem',textAlign:'center',color:'var(--text-3)'}}>
            <div style={{fontSize:'1rem',marginBottom:'0.5rem'}}>No HR record submitted</div>
            <div style={{fontSize:'0.8125rem'}}>This officer has not completed their HR self-service form yet.</div>
          </div>
        ) : (
          <div style={{padding:'0 0.5rem'}}>
            {/* Next of Kin */}
            <div style={{marginBottom:'1rem'}}>
              <div style={{fontSize:'0.6875rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.375rem'}}>Next of Kin / Emergency Contact</div>
              <Row label="Name" value={hr.nok_name} />
              <Row label="Relationship" value={hr.nok_relationship} />
              <Row label="Phone" value={hr.nok_phone} />
              {!hr.nok_name && <div style={{fontSize:'0.8125rem',color:'var(--text-3)',padding:'0.5rem 0'}}>Not provided</div>}
            </div>

            {/* Personal */}
            <div style={{marginBottom:'1rem'}}>
              <div style={{fontSize:'0.6875rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.375rem'}}>Personal Details</div>
              <Row label="Date of Birth" value={hr.date_of_birth ? new Date(hr.date_of_birth).toLocaleDateString('en-GB') : null} />
              <Row label="Address" value={[hr.address_line_1, hr.address_line_2, hr.city, hr.postcode].filter(Boolean).join(', ') || null} />
              <Row label="NI Number" value={hr.ni_number} />
            </div>

            {/* Documents */}
            <div style={{marginBottom:'1rem'}}>
              <div style={{fontSize:'0.6875rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.375rem'}}>Documents</div>
              {[
                { key: 'sia_front', label: 'SIA Licence (Front)' },
                { key: 'sia_back', label: 'SIA Licence (Back)' },
                { key: 'dbs_certificate', label: 'DBS Certificate' },
              ].map(doc => (
                <div key={doc.key} style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'0.5rem 0',borderBottom:'1px solid var(--border)'}}>
                  <div>
                    <span style={{fontSize:'0.8125rem',color:'var(--text-1)'}}>{doc.label}</span>
                    <span style={{fontSize:'0.75rem',color: hr[`${doc.key}_path`] ? '#10b981' : 'var(--text-3)',marginLeft:'0.5rem'}}>
                      {hr[`${doc.key}_path`] ? '● Uploaded' : '○ Not uploaded'}
                    </span>
                  </div>
                  {hr[`${doc.key}_path`] && (
                    <button className="btn btn-ghost btn-sm" onClick={() => viewDoc(doc.key)}>View</button>
                  )}
                </div>
              ))}
            </div>

            {/* GDPR */}
            <div style={{marginBottom:'0.5rem'}}>
              <div style={{fontSize:'0.6875rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.375rem'}}>GDPR Consent</div>
              <div style={{fontSize:'0.8125rem',color: hr.gdpr_consent ? '#10b981' : 'var(--danger)',fontWeight:600}}>
                {hr.gdpr_consent ? '✓ Consent given' : '✗ No consent recorded'}
                {hr.gdpr_consent_at && <span style={{color:'var(--text-3)',fontWeight:400,marginLeft:'0.5rem'}}>{new Date(hr.gdpr_consent_at).toLocaleDateString('en-GB')}</span>}
              </div>
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function UserFormModal({ user, onClose, onSaved }) {
  const SIA_TYPES = ['Security Guarding','Door Supervisor','CCTV Operator','Close Protection','Vehicle Immobiliser','Key Holding'];
  const [officerRates, setOfficerRates] = useState([]);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [allSites, setAllSites] = useState([]);
  const [showAddRate, setShowAddRate] = useState(false);
  const [editRateId, setEditRateId] = useState(null);
  const [rateForm, setRateForm] = useState({ site_id: '', hourly_rate: '', role_label: '' });

  useEffect(() => {
    if (!user) return;
    setRatesLoading(true);
    Promise.all([
      api.rates.list({ officer_id: user.id }),
      api.sites.list(),
    ]).then(([ratesRes, sitesRes]) => {
      setOfficerRates(ratesRes.data || []);
      setAllSites(sitesRes.data || []);
    }).catch(() => {}).finally(() => setRatesLoading(false));
  }, [user?.id]);

  async function saveRate() {
    if (!rateForm.hourly_rate) return;
    try {
      if (editRateId) {
        await api.rates.update(editRateId, { hourly_rate: parseFloat(rateForm.hourly_rate), site_id: rateForm.site_id || null, role_label: rateForm.role_label || null });
      } else {
        await api.rates.create({ officer_id: user.id, site_id: rateForm.site_id || null, hourly_rate: parseFloat(rateForm.hourly_rate), role_label: rateForm.role_label || null });
      }
      const res = await api.rates.list({ officer_id: user.id });
      setOfficerRates(res.data || []);
      setShowAddRate(false); setEditRateId(null); setRateForm({ site_id: '', hourly_rate: '', role_label: '' });
    } catch (err) { alert(err.message); }
  }

  async function deleteRate(id) {
    try {
      await api.rates.delete(id);
      setOfficerRates(prev => prev.filter(r => r.id !== id));
    } catch (err) { alert(err.message); }
  }

  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name:  user?.last_name  || '',
    email:      user?.email      || '',
    phone:      user?.phone      || '',
    role:            user?.role       || 'OFFICER',
    sia_licence_number: user?.sia_licence_number || '',
    sia_licence_type:   user?.sia_licence_type  || '',
    sia_expiry_date:    user?.sia_expiry_date ? user.sia_expiry_date.split('T')[0] : '',
    sia_licence_type_2:   user?.sia_licence_type_2  || '',
    sia_licence_number_2: user?.sia_licence_number_2 || '',
    sia_expiry_date_2:    user?.sia_expiry_date_2 ? user.sia_expiry_date_2.split('T')[0] : '',
    bs7858_clearance_date: user?.bs7858_clearance_date ? user.bs7858_clearance_date.split('T')[0] : '',
    bs7858_expiry_date:    user?.bs7858_expiry_date ? user.bs7858_expiry_date.split('T')[0] : '',
    employee_number: user?.employee_number || '',
    is_route_planner:  user?.is_route_planner || false,
    permissions: user?.permissions || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [generating, setGenerating] = useState(false);

  const PERMISSION_SECTIONS = [
    { key: 'operations', label: 'Operations', desc: 'Officers on duty, alerts, occurrence books, sites, reports' },
    { key: 'scheduling', label: 'Scheduling', desc: 'Roster, shift patterns' },
    { key: 'client_portal', label: 'Client Portal', desc: 'Portal settings' },
    { key: 'hr', label: 'HR', desc: 'Team, pay rates, company updates' },
    { key: 'site_config', label: 'Site Config', desc: 'Documents, instructions, patrol routes' },
    { key: 'pnl', label: 'P&L', desc: 'P&L dashboard, contracts' },
    { key: 'compliance', label: 'Compliance', desc: 'Policies' },
  ];

  function togglePermission(key) {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key) ? prev.permissions.filter(p => p !== key) : [...prev.permissions, key],
    }));
  }

  async function save() {
    if (!form.first_name.trim() || !form.email.trim()) { setError('Name and email are required'); return; }
    try {
      setSaving(true);
      const payload = {
        first_name: form.first_name, last_name: form.last_name,
        email: form.email.toLowerCase().trim(), phone: form.phone || null, role: form.role,
        sia_licence_number: form.sia_licence_number || null, sia_licence_type: form.sia_licence_type || null,
        sia_expiry_date: form.sia_expiry_date || null,
        sia_licence_type_2: form.sia_licence_type_2 || null, sia_licence_number_2: form.sia_licence_number_2 || null,
        sia_expiry_date_2: form.sia_expiry_date_2 || null,
        bs7858_clearance_date: form.bs7858_clearance_date || null, bs7858_expiry_date: form.bs7858_expiry_date || null,
        employee_number: form.employee_number || null,
        permissions: form.role === 'OFFICER' ? [] : form.permissions,
      };
      if (user) { await api.users.update(user.id, payload); onSaved(); }
      else { const res = await api.invite.send(payload); onSaved(res.message); }
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  const f = (k, v) => setForm(p => ({...p, [k]: v}));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:'640px',maxHeight:'90vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{user ? 'Edit Team Member' : 'Invite Team Member'}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field"><label className="label">First Name</label><input className="input" value={form.first_name} onChange={e=>f('first_name',e.target.value)} /></div>
          <div className="field"><label className="label">Last Name</label><input className="input" value={form.last_name} onChange={e=>f('last_name',e.target.value)} /></div>
          <div className="field" style={{gridColumn:'1/-1'}}><label className="label">Email</label><input type="email" className="input" value={form.email} onChange={e=>f('email',e.target.value)} disabled={!!user} />{!user && <div style={{fontSize:'0.75rem',color:'var(--text-2)',marginTop:'0.25rem'}}>An invitation email will be sent to this address</div>}</div>
          <div className="field"><label className="label">Phone</label><input className="input" value={form.phone} onChange={e=>f('phone',e.target.value)} /></div>
          <div className="field"><label className="label">Role</label><select className="input" value={form.role} onChange={e=>f('role',e.target.value)}><option value="OFFICER">Officer</option><option value="OPS_MANAGER">Ops Manager</option><option value="FD">Field Director</option><option value="COMPANY">Admin</option><option value="SUPER_ADMIN">Super Admin</option></select></div>
          <div className="field" style={{gridColumn:'1/-1'}}>
            <label className="label">Employee Number</label>
            <div style={{display:'flex',gap:'0.5rem'}}>
              <input className="input" style={{flex:1}} value={form.employee_number} onChange={e=>f('employee_number',e.target.value)} placeholder="e.g. RS-0001 or legacy number" />
              <button type="button" className="btn btn-secondary btn-sm" disabled={generating} onClick={async () => {
                setGenerating(true);
                try {
                  const res = await api.users.generateEmployeeNumber();
                  if (res?.employee_number) f('employee_number', res.employee_number);
                  else throw new Error('No number returned');
                } catch (e) { setError('Could not generate number: ' + e.message); }
                finally { setGenerating(false); }
              }}>{generating ? '...' : 'Auto-generate'}</button>
            </div>
          </div>

          {form.role !== 'OFFICER' && (
            <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}>
              <div className="section-title" style={{marginBottom:'0.5rem'}}>Dashboard Access</div>
              <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginBottom:'0.75rem'}}>Tick the sections this person can access. Super Admins have access to everything.</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.5rem'}}>
                {PERMISSION_SECTIONS.map(s => (
                  <label key={s.key} style={{display:'flex',alignItems:'flex-start',gap:'0.5rem',padding:'0.5rem 0.625rem',background: form.permissions.includes(s.key) ? 'rgba(26,82,168,0.06)' : 'var(--surface-2)',border: form.permissions.includes(s.key) ? '1px solid rgba(26,82,168,0.25)' : '1px solid var(--border)',borderRadius:'6px',cursor:'pointer'}}>
                    <input type="checkbox" checked={form.role === 'SUPER_ADMIN' || form.permissions.includes(s.key)} disabled={form.role === 'SUPER_ADMIN'}
                      onChange={() => togglePermission(s.key)} style={{marginTop:'2px',accentColor:'var(--blue)'}} />
                    <div>
                      <div style={{fontSize:'0.8125rem',fontWeight:600,color:'var(--text)'}}>{s.label}</div>
                      <div style={{fontSize:'0.6875rem',color:'var(--text-3)'}}>{s.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}><div className="section-title" style={{marginBottom:'0.25rem'}}>SIA Primary Licence</div></div>
          <div className="field"><label className="label">Licence Type</label><select className="input" value={form.sia_licence_type||''} onChange={e=>f('sia_licence_type',e.target.value)}><option value="">Select...</option>{SIA_TYPES.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
          <div className="field"><label className="label">Licence No.</label><input className="input" value={form.sia_licence_number} onChange={e=>f('sia_licence_number',e.target.value)} placeholder="16-digit number" /></div>
          <div className="field"><label className="label">Expiry</label><input type="date" className="input" value={form.sia_expiry_date} onChange={e=>f('sia_expiry_date',e.target.value)} /></div>

          <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}><div className="section-title" style={{marginBottom:'0.25rem'}}>SIA Second Licence (optional)</div></div>
          <div className="field"><label className="label">Licence Type</label><select className="input" value={form.sia_licence_type_2||''} onChange={e=>f('sia_licence_type_2',e.target.value)}><option value="">None</option>{SIA_TYPES.map(t=><option key={t} value={t}>{t}</option>)}</select></div>
          <div className="field"><label className="label">Licence No.</label><input className="input" value={form.sia_licence_number_2} onChange={e=>f('sia_licence_number_2',e.target.value)} placeholder="16-digit number" /></div>
          <div className="field"><label className="label">Expiry</label><input type="date" className="input" value={form.sia_expiry_date_2} onChange={e=>f('sia_expiry_date_2',e.target.value)} /></div>

          <div className="field" style={{gridColumn:'1/-1',borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.25rem'}}><div className="section-title" style={{marginBottom:'0.25rem'}}>BS7858 Vetting</div></div>
          <div className="field"><label className="label">Clearance Date</label><input type="date" className="input" value={form.bs7858_clearance_date} onChange={e=>f('bs7858_clearance_date',e.target.value)} /></div>
          <div className="field"><label className="label">Expiry Date</label><input type="date" className="input" value={form.bs7858_expiry_date} onChange={e=>f('bs7858_expiry_date',e.target.value)} /></div>
        </div>
        <div className="field">
          <label style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer'}}>
            <input type="checkbox" checked={form.is_route_planner||false} onChange={e=>setForm(f=>({...f,is_route_planner:e.target.checked}))} style={{width:'1rem',height:'1rem',accentColor:'var(--blue)'}} />
            <span className="label" style={{margin:0}}>Route Planner — can create &amp; edit patrol routes</span>
          </label>
        </div>
        {/* Pay Rates */}
        {user && (
          <div style={{borderTop:'1px solid var(--border)',paddingTop:'0.75rem',marginTop:'0.75rem'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.5rem'}}>
              <div className="section-title" style={{margin:0}}>Pay Rates</div>
              {!showAddRate && <button className="btn btn-ghost btn-sm" onClick={() => { setEditRateId(null); setRateForm({ site_id:'', hourly_rate:'', role_label:'' }); setShowAddRate(true); }}>+ Add Rate</button>}
            </div>
            {ratesLoading ? <div style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>Loading...</div> : officerRates.length === 0 && !showAddRate ? (
              <div style={{fontSize:'0.8125rem',color:'var(--text-3)',padding:'0.5rem 0'}}>No pay rates set</div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:'0.375rem',marginBottom: showAddRate ? '0.75rem' : 0}}>
                {officerRates.map(r => (
                  <div key={r.id} style={{display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.5rem 0.75rem',background:'var(--surface-2)',borderRadius:'6px',fontSize:'0.8125rem'}}>
                    <span style={{flex:1,fontWeight:500}}>{r.site?.name || 'All sites (default)'}</span>
                    <span style={{fontWeight:700,color:'var(--text)'}}>£{parseFloat(r.hourly_rate||0).toFixed(2)}/hr</span>
                    {r.role_label && <span style={{color:'var(--text-3)',fontSize:'0.75rem'}}>{r.role_label}</span>}
                    <button className="btn btn-ghost btn-sm" style={{padding:'0.25rem 0.5rem',fontSize:'0.75rem'}} onClick={() => { setEditRateId(r.id); setRateForm({ site_id: r.site_id||'', hourly_rate: r.hourly_rate||'', role_label: r.role_label||'' }); setShowAddRate(true); }}>Edit</button>
                    <button className="btn btn-ghost btn-sm" style={{padding:'0.25rem 0.5rem',fontSize:'0.75rem',color:'var(--danger)'}} onClick={() => deleteRate(r.id)}>Delete</button>
                  </div>
                ))}
              </div>
            )}
            {showAddRate && (
              <div style={{padding:'0.75rem',background:'var(--surface-2)',borderRadius:'8px',border:'1px solid var(--border)'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0.5rem',marginBottom:'0.5rem'}}>
                  <div className="field"><label className="label">Site</label><select className="input" value={rateForm.site_id} onChange={e => setRateForm(p=>({...p, site_id:e.target.value}))}><option value="">All sites (default)</option>{allSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
                  <div className="field"><label className="label">Rate (£/hr)</label><input type="number" step="0.01" min="0" className="input" value={rateForm.hourly_rate} onChange={e => setRateForm(p=>({...p, hourly_rate:e.target.value}))} placeholder="12.50" /></div>
                  <div className="field"><label className="label">Role Label</label><input className="input" value={rateForm.role_label} onChange={e => setRateForm(p=>({...p, role_label:e.target.value}))} placeholder="e.g. SG, DS" /></div>
                </div>
                <div style={{display:'flex',gap:'0.5rem',justifyContent:'flex-end'}}>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setShowAddRate(false); setEditRateId(null); }}>Cancel</button>
                  <button className="btn btn-primary btn-sm" onClick={saveRate} disabled={!rateForm.hourly_rate}>{editRateId ? 'Update' : 'Add Rate'}</button>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? (user ? 'Saving...' : 'Sending invite...') : (user ? 'Save' : 'Send Invite')}</button>
        </div>
      </div>
    </div>
  );
}


function Reporting({ user }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('30');
  const [reportTab, setReportTab] = useState('logs');

  useEffect(() => {
    async function load() {
      try {
        const from = new Date();
        from.setDate(from.getDate() - parseInt(dateRange));
        const res = await api.logs.list({ from: from.toISOString(), limit: 500 });
        setLogs(res.data || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [dateRange]);

  const byType = logs.reduce((acc, l) => { acc[l.log_type] = (acc[l.log_type]||0)+1; return acc; }, {});
  const bySite = logs.reduce((acc, l) => { const n = l.site?.name||'Unknown'; acc[n]=(acc[n]||0)+1; return acc; }, {});
  const topTypes = Object.entries(byType).sort((a,b)=>b[1]-a[1]);
  const topSites = Object.entries(bySite).sort((a,b)=>b[1]-a[1]);
  const maxCount = Math.max(...Object.values(byType), 1);

  const typeColors = {
    PATROL:'#1a52a8', INCIDENT:'#dc2626', ALARM:'#d97706',
    ACCESS:'#7c3aed', VISITOR:'#0891b2', HANDOVER:'#15803d',
    MAINTENANCE:'#ea580c', VEHICLE:'#64748b', GENERAL:'#94a3b8',
  };

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Reports</div>
        <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
          <button
            className="btn btn-secondary btn-sm"
            disabled={logs.length === 0}
            onClick={async () => {
              const token = await window.Clerk?.session?.getToken();
              if (!token) return;
              const from = new Date();
              from.setDate(from.getDate() - parseInt(dateRange));
              const url = import.meta.env.VITE_API_URL + '/api/logs/export?from=' + from.toISOString();
              const res = await fetch(url, { headers: { Authorization: 'Bearer ' + token } });
              const blob = await res.blob();
              const a = document.createElement('a');
              a.href = URL.createObjectURL(blob);
              a.download = 'doblive-logs.csv';
              a.click();
            }}
          >
            Export CSV
          </button>
          <select className="input" style={{width:'140px'}} value={dateRange} onChange={e => setDateRange(e.target.value)}>
            <option value="7">Last 7 days</option>
            <option value="14">Last 14 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
      </div>
      <div style={{display:'flex',gap:0,borderBottom:'1px solid var(--border)',padding:'0 1.5rem',background:'var(--surface)'}}>
        {[{key:'logs',label:'Reports'},{key:'visitors',label:'Visitors & Contractors'}].map(t => (
          <button key={t.key} onClick={() => setReportTab(t.key)}
            style={{padding:'0.75rem 1rem',background:'none',border:'none',borderBottom:`2px solid ${reportTab===t.key?'var(--blue)':'transparent'}`,color:reportTab===t.key?'var(--blue)':'var(--text-2)',fontSize:'0.875rem',fontWeight:600,cursor:'pointer',marginBottom:'-1px',whiteSpace:'nowrap'}}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="page-content">
        {reportTab === 'visitors' && <VisitorReportTab />}
        {reportTab === 'logs' && loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
        ) : reportTab === 'logs' ? (
          <>
            <div className="stats-grid" style={{marginBottom:'1.5rem'}}>
              <div className="stat-card"><div className="stat-value">{logs.length}</div><div className="stat-label">Total Logs</div></div>
              <div className="stat-card"><div className="stat-value" style={{color:'var(--danger)'}}>{byType.INCIDENT||0}</div><div className="stat-label">Incidents</div></div>
              <div className="stat-card"><div className="stat-value" style={{color:'#d97706'}}>{byType.ALARM||0}</div><div className="stat-label">Alarms</div></div>
              <div className="stat-card"><div className="stat-value" style={{color:'var(--blue)'}}>{byType.PATROL||0}</div><div className="stat-label">Patrols</div></div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1.25rem'}}>
              <div className="card">
                <div className="section-title" style={{marginBottom:'1rem'}}>Logs by Type</div>
                {topTypes.length === 0 ? <div className="empty-state"><p>No data</p></div> : (
                  <div style={{display:'flex',flexDirection:'column',gap:'0.625rem'}}>
                    {topTypes.map(([type, count]) => (
                      <div key={type} style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
                        <span style={{fontSize:'0.75rem',fontWeight:500,color:'var(--text-2)',width:'5rem',textAlign:'right',flexShrink:0}}>
                          {type.charAt(0)+type.slice(1).toLowerCase()}
                        </span>
                        <div style={{flex:1,background:'var(--surface-2)',borderRadius:'2px',height:'8px',overflow:'hidden'}}>
                          <div style={{width:((count/maxCount)*100)+'%',height:'100%',background:typeColors[type]||'#94a3b8',borderRadius:'2px'}} />
                        </div>
                        <span style={{fontSize:'0.75rem',fontWeight:600,width:'1.5rem',textAlign:'right'}}>{count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="card">
                <div className="section-title" style={{marginBottom:'1rem'}}>Logs by Site</div>
                {topSites.length === 0 ? <div className="empty-state"><p>No data</p></div> : (
                  <table className="table">
                    <tbody>
                      {topSites.map(([site, count]) => (
                        <tr key={site}>
                          <td style={{padding:'0.5rem 0'}}>{site}</td>
                          <td style={{padding:'0.5rem 0',textAlign:'right',fontWeight:600}}>{count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function visitorStatusBadge(status) {
  if (status === 'on_site') return <span className="badge badge-success">ON SITE</span>;
  if (status === 'signed_out_by_office') return <span className="badge badge-neutral">OFFICE SIGN-OUT</span>;
  return <span className="badge badge-neutral">OFF SITE</span>;
}

function visitorDuration(time_in) {
  const mins = Math.floor((Date.now() - new Date(time_in)) / 60000);
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function VisitorReportTab() {
  const [visitors, setVisitors] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [signingOut, setSigningOut] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (siteFilter) params.site_id = siteFilter;
      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      const [vRes, sRes] = await Promise.all([api.visitors.list(params), api.sites.list()]);
      setVisitors(vRes.data || []);
      setSites(sRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [siteFilter, statusFilter]);

  const filtered = search
    ? visitors.filter(v => { const q = search.toLowerCase(); return (v.visitor_name||'').toLowerCase().includes(q) || (v.company_name||'').toLowerCase().includes(q) || (v.vehicle_reg||'').toLowerCase().includes(q); })
    : visitors;

  function exportCSV() {
    const headers = ['Name','Company','Site','Pass','Vehicle','Time In','Time Out','Status','Officer'];
    const rows = filtered.map(v => [v.visitor_name, v.company_name||'', v.site?.name||'', v.pass_number||'', v.vehicle_reg||'',
      v.time_in ? new Date(v.time_in).toLocaleString('en-GB',{timeZone:'Europe/London'}) : '',
      v.time_out ? new Date(v.time_out).toLocaleString('en-GB',{timeZone:'Europe/London'}) : '',
      v.status, v.officer ? `${v.officer.first_name} ${v.officer.last_name}` : '']);
    const csv = [headers,...rows].map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `visitors-all-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  }

  const fmtTime = t => t ? new Date(t).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'}) : '—';
  const fmtDate = t => t ? new Date(t).toLocaleDateString('en-GB',{day:'2-digit',month:'short',timeZone:'Europe/London'}) : '';

  async function handleSignOut(v) {
    if (!window.confirm(`Sign out ${v.visitor_name}? They have been on site for ${visitorDuration(v.time_in)}.`)) return;
    setSigningOut(v.id);
    try {
      await api.visitors.signOut(v.id);
      await load();
    } catch (err) { alert('Sign-out failed: ' + err.message); }
    finally { setSigningOut(null); }
  }

  if (loading) return <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>;

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'1rem',flexWrap:'wrap'}}>
        <input className="input" style={{width:'200px'}} placeholder="Search name, company, reg..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="input" style={{width:'150px'}} value={siteFilter} onChange={e => setSiteFilter(e.target.value)}>
          <option value="">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <div style={{display:'flex',gap:'2px',background:'var(--surface-2)',borderRadius:'6px',padding:'2px'}}>
          {[['','All'],['on_site','On Site']].map(([k,l]) => (
            <button key={k} onClick={() => setStatusFilter(k)}
              style={{padding:'0.375rem 0.75rem',borderRadius:'4px',border:'none',cursor:'pointer',fontSize:'0.8125rem',fontWeight:600,
                background:statusFilter===k?'var(--blue)':'transparent',color:statusFilter===k?'#fff':'var(--text-2)'}}>
              {l}
            </button>
          ))}
        </div>
        <span style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>{filtered.length} visitor{filtered.length!==1?'s':''}</span>
        <div style={{flex:1}} />
        <button className="btn btn-secondary btn-sm" onClick={exportCSV} disabled={filtered.length===0}>Export CSV</button>
      </div>
      {filtered.length === 0 ? (
        <div className="empty-state"><p>No visitors found</p></div>
      ) : (
        <table className="table">
          <thead><tr><th>Name</th><th>Company</th><th>Site</th><th>Pass</th><th>Vehicle</th><th>Time In</th><th>Time Out</th><th>Status</th><th>Officer</th><th></th></tr></thead>
          <tbody>
            {filtered.map(v => (
              <tr key={v.id}>
                <td style={{fontWeight:500}}>{v.visitor_name}</td>
                <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{v.company_name||v.who_visiting||'—'}</td>
                <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{v.site?.name||'—'}</td>
                <td style={{fontFamily:'monospace',fontSize:'0.8125rem',color:'var(--text-2)'}}>{v.pass_number||'—'}</td>
                <td style={{fontFamily:'monospace',fontSize:'0.8125rem',color:'var(--text-2)'}}>{v.vehicle_reg||'—'}</td>
                <td style={{fontSize:'0.8125rem',color:'var(--text-2)',whiteSpace:'nowrap'}}>{fmtDate(v.time_in)} {fmtTime(v.time_in)}</td>
                <td style={{fontSize:'0.8125rem',color:'var(--text-2)',whiteSpace:'nowrap'}}>{v.time_out ? `${fmtDate(v.time_out)} ${fmtTime(v.time_out)}` : '—'}</td>
                <td>{visitorStatusBadge(v.status)}</td>
                <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{v.officer?`${v.officer.first_name} ${v.officer.last_name}`:'—'}</td>
                <td>{v.status === 'on_site' && (
                  <button
                    onClick={() => handleSignOut(v)}
                    disabled={signingOut === v.id}
                    style={{padding:'0.25rem 0.625rem',background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:'5px',color:'var(--danger)',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}
                  >{signingOut === v.id ? '…' : 'Sign out'}</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function OnDutyScreen({ user }) {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const res = await api.shifts.list({ status: 'ACTIVE', limit: 100 });
      setShifts(res.data || []);
    } catch(e){ console.error(e); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function forceEnd(shift) {
    if (!window.confirm(`Force end shift for ${shift.officer?.first_name} ${shift.officer?.last_name}?`)) return;
    await api.shifts.update(shift.id, { status: 'COMPLETED', checked_out_at: new Date().toISOString() });
    load();
  }

  const onDuration = s => {
    const start = new Date(s.checked_in_at || s.start_time);
    const mins = Math.floor((new Date() - start) / 60000);
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Officers On Duty</div>
        <button className="btn btn-secondary btn-sm" onClick={load}>Refresh</button>
      </div>
      <div className="page-content">
        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner"/></div>
        ) : shifts.length === 0 ? (
          <div className="empty-state">
            <div style={{fontSize:'2rem',marginBottom:'0.75rem'}}>💤</div>
            <p>No officers currently on duty</p>
          </div>
        ) : (
          <>
            <div style={{marginBottom:'1rem',fontSize:'0.875rem',color:'var(--text-2)'}}>
              <span style={{color:'var(--success)',fontWeight:700}}>{shifts.length}</span> officer{shifts.length!==1?'s':''} on duty
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:'0.75rem'}}>
              {shifts.map(s => (
                <div key={s.id} className="card" style={{borderLeft:'3px solid var(--success)'}}>
                  <div style={{display:'flex',alignItems:'center',gap:'1rem'}}>
                    <div style={{width:'40px',height:'40px',borderRadius:'50%',background:'var(--blue)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:'0.9375rem',flexShrink:0}}>
                      {s.officer?.first_name?.[0]}{s.officer?.last_name?.[0]}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:600,fontSize:'0.9375rem'}}>
                        {s.officer ? `${s.officer.first_name} ${s.officer.last_name}` : 'Unknown'}
                      </div>
                      <div style={{fontSize:'0.8125rem',color:'var(--text-2)'}}>
                        {s.site?.name || '—'}
                      </div>
                      <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginTop:'0.125rem'}}>
                        On since {new Date(s.checked_in_at||s.start_time).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})} · {onDuration(s)}
                      </div>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:'0.375rem',flexShrink:0}}>
                      <span className="badge badge-success">ON DUTY</span>
                      <button
                        onClick={() => forceEnd(s)}
                        style={{padding:'0.375rem 0.75rem',background:'rgba(220,38,38,0.1)',border:'1px solid rgba(220,38,38,0.25)',borderRadius:'6px',color:'var(--danger)',fontSize:'0.75rem',fontWeight:600,cursor:'pointer'}}
                      >
                        Force End
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SiteVisitorsTab({ siteId }) {
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('on_site');
  const [signingOut, setSigningOut] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const params = { site_id: siteId, limit: 200 };
      if (filter === 'on_site') params.status = 'on_site';
      if (filter === 'today') {
        const today = new Date(); today.setHours(0,0,0,0);
        params.from = today.toISOString();
      }
      const res = await api.visitors.list(params);
      setVisitors(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [siteId, filter]);

  const filtered = search
    ? visitors.filter(v => {
        const q = search.toLowerCase();
        return (v.visitor_name||'').toLowerCase().includes(q) || (v.company_name||'').toLowerCase().includes(q);
      })
    : visitors;

  function exportCSV() {
    const headers = ['Name','Company','Pass','Vehicle','Personnel','Time In','Time Out','Status','Officer'];
    const rows = filtered.map(v => [
      v.visitor_name, v.company_name||'', v.pass_number||'', v.vehicle_reg||'', v.personnel_count,
      v.time_in ? new Date(v.time_in).toLocaleString('en-GB',{timeZone:'Europe/London'}) : '',
      v.time_out ? new Date(v.time_out).toLocaleString('en-GB',{timeZone:'Europe/London'}) : '',
      v.status, v.officer ? `${v.officer.first_name} ${v.officer.last_name}` : '',
    ]);
    const csv = [headers,...rows].map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `visitors-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  }

  const fmtTime = t => t ? new Date(t).toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit',timeZone:'Europe/London'}) : '—';
  const fmtDate = t => t ? new Date(t).toLocaleDateString('en-GB',{day:'2-digit',month:'short',timeZone:'Europe/London'}) : '';

  async function handleSignOut(v) {
    if (!window.confirm(`Sign out ${v.visitor_name}? They have been on site for ${visitorDuration(v.time_in)}.`)) return;
    setSigningOut(v.id);
    try {
      await api.visitors.signOut(v.id);
      await load();
    } catch (err) { alert('Sign-out failed: ' + err.message); }
    finally { setSigningOut(null); }
  }

  if (loading) return <div style={{display:'flex',justifyContent:'center',padding:'2rem'}}><div className="spinner" /></div>;

  return (
    <div className="card">
      <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.75rem',flexWrap:'wrap'}}>
        <input className="input" style={{width:'180px'}} placeholder="Search name, company..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{display:'flex',gap:'2px',background:'var(--surface-2)',borderRadius:'6px',padding:'2px'}}>
          {[['','All'],['on_site','On Site'],['today','Today']].map(([k,l]) => (
            <button key={k} onClick={() => setFilter(k)}
              style={{padding:'0.375rem 0.75rem',borderRadius:'4px',border:'none',cursor:'pointer',fontSize:'0.8125rem',fontWeight:600,
                background:filter===k?'var(--blue)':'transparent',color:filter===k?'#fff':'var(--text-2)'}}>
              {l}
            </button>
          ))}
        </div>
        <span style={{fontSize:'0.75rem',color:'var(--text-3)'}}>{filtered.length} visitor{filtered.length!==1?'s':''}</span>
        <div style={{flex:1}} />
        <button className="btn btn-secondary btn-sm" onClick={exportCSV} disabled={filtered.length===0}>Export CSV</button>
      </div>
      {filtered.length === 0 ? (
        <div className="empty-state"><p>No visitors found</p></div>
      ) : (
        <table className="table">
          <thead><tr><th>Name</th><th>Company</th><th>Pass</th><th>Vehicle</th><th>In</th><th>Out</th><th>Status</th><th>Officer</th><th></th></tr></thead>
          <tbody>
            {filtered.map(v => (
              <tr key={v.id}>
                <td style={{fontWeight:500}}>{v.visitor_name}</td>
                <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{v.company_name||v.who_visiting||'—'}</td>
                <td style={{fontFamily:'monospace',fontSize:'0.8125rem',color:'var(--text-2)'}}>{v.pass_number||'—'}</td>
                <td style={{fontFamily:'monospace',fontSize:'0.8125rem',color:'var(--text-2)'}}>{v.vehicle_reg||'—'}</td>
                <td style={{fontSize:'0.8125rem',color:'var(--text-2)',whiteSpace:'nowrap'}}>{fmtDate(v.time_in)} {fmtTime(v.time_in)}</td>
                <td style={{fontSize:'0.8125rem',color:'var(--text-2)',whiteSpace:'nowrap'}}>{v.time_out ? `${fmtDate(v.time_out)} ${fmtTime(v.time_out)}` : '—'}</td>
                <td>{visitorStatusBadge(v.status)}</td>
                <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{v.officer?`${v.officer.first_name} ${v.officer.last_name}`:'—'}</td>
                <td>{v.status === 'on_site' && (
                  <button
                    onClick={() => handleSignOut(v)}
                    disabled={signingOut === v.id}
                    style={{padding:'0.25rem 0.625rem',background:'rgba(220,38,38,0.08)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:'5px',color:'var(--danger)',fontSize:'0.75rem',fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}
                  >{signingOut === v.id ? '…' : 'Sign out'}</button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SiteDocumentsTab({ siteId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  function load() {
    setLoading(true);
    api.folders.documents.list({ site_id: siteId })
      .then(res => setDocs(res.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, [siteId]);

  async function viewDoc(doc) {
    try {
      const res = await api.folders.documents.getSigned(doc.id);
      if (res.data?.url) window.open(res.data.url, '_blank');
      else alert('Could not open document');
    } catch { alert('Could not open document'); }
  }

  async function downloadDoc(doc) {
    try {
      const res = await api.folders.documents.getSigned(doc.id);
      if (res.data?.url) {
        const a = document.createElement('a');
        a.href = res.data.url;
        a.download = doc.original_name || doc.name || 'document.pdf';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch { alert('Could not download document'); }
  }

  async function deleteDoc(doc) {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    try {
      await api.folders.documents.delete(doc.id);
      load();
    } catch (e) { alert('Could not delete: ' + e.message); }
  }

  if (loading) return <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>;

  return (
    <div>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem'}}>
        <div style={{fontSize:'0.875rem',fontWeight:700,color:'var(--text-1)'}}>Property Inspections ({docs.length})</div>
      </div>
      {docs.length === 0 ? (
        <div className="empty-state"><p>No inspection reports for this site yet</p></div>
      ) : (
        <table className="table">
          <thead><tr><th>Report</th><th>Inspector</th><th>Date</th><th></th></tr></thead>
          <tbody>
            {docs.map(doc => (
              <tr key={doc.id}>
                <td>
                  <div style={{fontWeight:600}}>{doc.name || doc.original_name}</div>
                </td>
                <td style={{fontSize:'0.8125rem',color:'var(--text-2)'}}>
                  {doc.uploader ? `${doc.uploader.first_name} ${doc.uploader.last_name}` : '—'}
                </td>
                <td style={{fontSize:'0.8125rem',color:'var(--text-2)'}}>
                  {doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-GB', {day:'2-digit',month:'short',year:'numeric'}) : '—'}
                </td>
                <td style={{textAlign:'right',display:'flex',gap:'0.375rem',justifyContent:'flex-end'}}>
                  <button className="btn btn-ghost btn-sm" style={{color:'var(--blue)'}} onClick={() => viewDoc(doc)}>View</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => downloadDoc(doc)}>Download</button>
                  <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={() => deleteDoc(doc)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function SiteCodesTab({ siteId }) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editCode, setEditCode] = useState(null);
  const [form, setForm] = useState({ label: '', code: '', code_type: 'keypad', notes: '' });
  const [saving, setSaving] = useState(false);
  const [visibleCodes, setVisibleCodes] = useState(new Set());

  async function load() {
    try {
      const res = await api.sites.codes.list(siteId);
      setCodes(res.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [siteId]);

  function openForm(c) {
    if (c) {
      setEditCode(c);
      setForm({ label: c.label, code: c.code, code_type: c.code_type, notes: c.notes || '' });
    } else {
      setEditCode(null);
      setForm({ label: '', code: '', code_type: 'keypad', notes: '' });
    }
    setShowForm(true);
  }

  async function save() {
    if (!form.label.trim() || !form.code.trim()) return;
    setSaving(true);
    try {
      if (editCode) {
        await api.sites.codes.update(siteId, editCode.id, form);
      } else {
        await api.sites.codes.create(siteId, form);
      }
      setShowForm(false);
      load();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function remove(codeId) {
    if (!window.confirm('Delete this code?')) return;
    await api.sites.codes.delete(siteId, codeId);
    load();
  }

  function toggleVisible(id) {
    setVisibleCodes(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const codeTypeLabels = { keypad: 'Keypad', padlock: 'Padlock', key_safe: 'Key Safe', door: 'Door Code', gate: 'Gate', alarm: 'Alarm', other: 'Other' };

  if (loading) return <div style={{display:'flex',justifyContent:'center',padding:'2rem'}}><div className="spinner" /></div>;

  return (
    <div className="card">
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.875rem'}}>
        <div className="section-title">Site Codes</div>
        <button className="btn btn-primary btn-sm" onClick={() => openForm(null)}>
          <PlusIcon style={{width:'0.875rem',height:'0.875rem'}} /> Add Code
        </button>
      </div>
      {codes.length === 0 ? (
        <div className="empty-state"><p>No codes stored for this site. Add keypad, padlock, key safe or door codes.</p></div>
      ) : (
        <table className="table">
          <thead>
            <tr><th>Label</th><th>Type</th><th>Code</th><th>Notes</th><th></th></tr>
          </thead>
          <tbody>
            {codes.map(c => (
              <tr key={c.id}>
                <td style={{fontWeight:500}}>{c.label}</td>
                <td style={{fontSize:'0.8125rem',color:'var(--text-2)'}}>{codeTypeLabels[c.code_type] || c.code_type}</td>
                <td style={{fontFamily:'monospace',fontSize:'0.875rem'}}>
                  <span style={{display:'inline-flex',alignItems:'center',gap:'0.5rem'}}>
                    {visibleCodes.has(c.id) ? c.code : '••••••'}
                    <button onClick={() => toggleVisible(c.id)}
                      style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-3)',fontSize:'0.8125rem',padding:0}}>
                      {visibleCodes.has(c.id) ? 'Hide' : 'Show'}
                    </button>
                  </span>
                </td>
                <td style={{fontSize:'0.8125rem',color:'var(--text-2)'}}>{c.notes || '—'}</td>
                <td style={{textAlign:'right',display:'flex',gap:'0.375rem',justifyContent:'flex-end'}}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openForm(c)}>Edit</button>
                  <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={() => remove(c.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">{editCode ? 'Edit Code' : 'Add Code'}</div>
              <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
              <div className="field">
                <label className="label">Label *</label>
                <input className="input" value={form.label} onChange={e => setForm(f=>({...f,label:e.target.value}))} placeholder="e.g. Main entrance keypad" />
              </div>
              <div className="field">
                <label className="label">Type</label>
                <select className="input" value={form.code_type} onChange={e => setForm(f=>({...f,code_type:e.target.value}))}>
                  {Object.entries(codeTypeLabels).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div className="field">
                <label className="label">Code *</label>
                <input className="input" value={form.code} onChange={e => setForm(f=>({...f,code:e.target.value}))} placeholder="e.g. 1234#" />
              </div>
              <div className="field">
                <label className="label">Notes</label>
                <input className="input" value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} placeholder="Optional notes" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving || !form.label.trim() || !form.code.trim()}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InfoField({ label, value, span }) {
  return (
    <div style={span ? {gridColumn:'1/-1'} : {}}>
      <div style={{fontSize:'0.75rem',color:'var(--text-2)',fontWeight:500,marginBottom:'0.25rem'}}>{label}</div>
      <div style={{fontSize:'0.875rem',color:value ? 'var(--text)' : 'var(--text-3)'}}>{value || 'Not set'}</div>
    </div>
  );
}

// ── ID Cards Summary Card ──────────────────────────────────────────────────
function IDCardsSummaryCard() {
  const [summary, setSummary] = useState(null);
  const [cards, setCards] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.idCards.summary().then(setSummary).catch(() => {});
  }, []);

  async function expand() {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (!cards) {
      try { const res = await api.idCards.company(); setCards(res.data || []); }
      catch { setCards([]); }
    }
  }

  if (!summary) return null;

  const STATUS_DOT = { active: '#16a34a', lost: '#f59e0b', revoked: '#ef4444', returned: '#9ca3af', expired: '#991b1b' };

  return (
    <div className="card" style={{marginBottom:'1rem',padding:'0.875rem 1rem',cursor:'pointer'}} onClick={expand}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:'1rem'}}>
          <div style={{fontSize:'0.8125rem',fontWeight:700,color:'var(--text)'}}>ID Cards</div>
          <div style={{display:'flex',gap:'0.75rem',fontSize:'0.75rem'}}>
            <span style={{color:'#16a34a',fontWeight:600}}>{summary.active} Active</span>
            {summary.expiring_soon > 0 && <span style={{color:'#ef4444',fontWeight:700}}>{summary.expiring_soon} Expiring Soon</span>}
            {summary.expired > 0 && <span style={{color:'#991b1b',fontWeight:700}}>{summary.expired} Expired</span>}
            {summary.lost > 0 && <span style={{color:'#f59e0b',fontWeight:600}}>{summary.lost} Lost</span>}
            {summary.revoked > 0 && <span style={{color:'#ef4444',fontWeight:600}}>{summary.revoked} Revoked</span>}
          </div>
        </div>
        <span style={{color:'var(--text-3)',fontSize:'0.75rem'}}>{expanded ? '▾' : '▸'}</span>
      </div>
      {expanded && cards && (
        <div style={{marginTop:'0.75rem',borderTop:'1px solid var(--border)',paddingTop:'0.75rem'}} onClick={e => e.stopPropagation()}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8125rem'}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)'}}>
                <th style={{textAlign:'left',padding:'0.375rem 0.5rem',fontWeight:600,color:'var(--text-3)',fontSize:'0.6875rem'}}>Officer</th>
                <th style={{textAlign:'left',padding:'0.375rem 0.5rem',fontWeight:600,color:'var(--text-3)',fontSize:'0.6875rem'}}>Card #</th>
                <th style={{textAlign:'center',padding:'0.375rem 0.5rem',fontWeight:600,color:'var(--text-3)',fontSize:'0.6875rem'}}>Status</th>
                <th style={{textAlign:'right',padding:'0.375rem 0.5rem',fontWeight:600,color:'var(--text-3)',fontSize:'0.6875rem'}}>Expiry</th>
              </tr>
            </thead>
            <tbody>
              {cards.map(c => {
                const daysLeft = Math.ceil((new Date(c.expiry_date) - new Date()) / 86400000);
                const isExpiring = daysLeft <= 30 && daysLeft > 0;
                const isExpired = daysLeft <= 0;
                return (
                  <tr key={c.id} style={{borderBottom:'1px solid var(--border)'}}>
                    <td style={{padding:'0.375rem 0.5rem',fontWeight:500}}>
                      {c.officer?.first_name} {c.officer?.last_name}
                      {c.officer?.employee_number && <span style={{color:'var(--text-3)',fontSize:'0.6875rem',marginLeft:'0.25rem'}}>({c.officer.employee_number})</span>}
                    </td>
                    <td style={{padding:'0.375rem 0.5rem',color:'var(--blue)',fontWeight:600}}>{c.card_number}</td>
                    <td style={{padding:'0.375rem 0.5rem',textAlign:'center'}}>
                      <span style={{display:'inline-flex',alignItems:'center',gap:'4px'}}>
                        <span style={{width:6,height:6,borderRadius:'50%',background:STATUS_DOT[c.status] || '#9ca3af'}} />
                        <span style={{fontSize:'0.6875rem',fontWeight:600,textTransform:'capitalize'}}>{c.status}</span>
                      </span>
                    </td>
                    <td style={{padding:'0.375rem 0.5rem',textAlign:'right',color: isExpired ? '#991b1b' : isExpiring ? '#ef4444' : 'var(--text-2)', fontWeight: isExpiring || isExpired ? 700 : 400}}>
                      {new Date(c.expiry_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'2-digit'})}
                      {isExpiring && <span style={{fontSize:'0.625rem',marginLeft:'0.25rem'}}>({daysLeft}d)</span>}
                      {isExpired && <span style={{fontSize:'0.625rem',marginLeft:'0.25rem'}}>EXPIRED</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export { OnDutyScreen };

export { ManagerDashboard };
export { SiteManagement };
export { LogReview };
export { TaskAssignment };
export { SiteDetail };
export { TeamManagement };
export { Reporting };

// ── Shared Patrol Session Map Modal ─────────────────────────────────────────
function PatrolSessionModal({ session, onClose }) {
  const mapRef = useRef(null);
  const mapInstance = useRef(null);

  const fmtTime = t => t ? new Date(t).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit', timeZone:'Europe/London' }) : '—';
  const fmtDate = t => t ? new Date(t).toLocaleDateString('en-GB', { weekday:'short', day:'2-digit', month:'short', year:'numeric', timeZone:'Europe/London' }) : '—';

  function calcDistance(trail) {
    if (!trail?.length || trail.length < 2) return null;
    let total = 0;
    for (let i = 1; i < trail.length; i++) {
      const R = 6371000;
      const lat1 = trail[i-1].lat * Math.PI/180, lat2 = trail[i].lat * Math.PI/180;
      const dLat = lat2 - lat1, dLng = (trail[i].lng - trail[i-1].lng) * Math.PI/180;
      const a = Math.sin(dLat/2)**2 + Math.cos(lat1)*Math.cos(lat2)*Math.sin(dLng/2)**2;
      total += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    }
    return Math.round(total);
  }

  useEffect(() => {
    if (!session || !mapRef.current || mapInstance.current) return;
    function renderMap() {
      if (!window.L || !mapRef.current) return;
      const L = window.L;
      const map = L.map(mapRef.current, { zoomControl: true, attributionControl: false });
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { maxZoom: 19 }).addTo(map);
      const allPoints = [];

      if (session.gps_trail?.length) {
        const trail = session.gps_trail.filter(p => p.lat && p.lng).map(p => [p.lat, p.lng]);
        if (trail.length > 0) {
          L.polyline(trail, { color: '#3b82f6', weight: 3, opacity: 0.9 }).addTo(map);
          L.circleMarker(trail[0], { radius: 8, fillColor: '#22c55e', fillOpacity: 1, color: '#fff', weight: 2 }).addTo(map).bindPopup('Start');
          L.circleMarker(trail[trail.length-1], { radius: 8, fillColor: '#ef4444', fillOpacity: 1, color: '#fff', weight: 2 }).addTo(map).bindPopup('End');
          allPoints.push(...trail);
        }
      }

      if (session.checkpoints_completed?.length) {
        session.checkpoints_completed.forEach((cp, i) => {
          if (!cp.lat || !cp.lng) return;
          const icon = L.divIcon({ html: `<div style="width:22px;height:22px;background:#a78bfa;border:2px solid #fff;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:700;color:#0b1222">${i+1}</div>`, iconSize:[22,22], iconAnchor:[11,11] });
          L.marker([cp.lat, cp.lng], { icon }).addTo(map).bindPopup(cp.name || `Checkpoint ${i+1}`);
          allPoints.push([cp.lat, cp.lng]);
        });
      }

      if (allPoints.length > 0) map.fitBounds(allPoints, { padding: [30, 30] });
      else map.setView([52.48, -1.89], 14);
      mapInstance.current = map;
    }

    if (window.L) { renderMap(); }
    else {
      if (!document.getElementById('leaflet-css')) {
        const link = document.createElement('link'); link.id='leaflet-css'; link.rel='stylesheet';
        link.href='https://unpkg.com/leaflet@1.9.4/dist/leaflet.css'; document.head.appendChild(link);
      }
      if (!document.getElementById('leaflet-js')) {
        const script = document.createElement('script'); script.id='leaflet-js';
        script.src='https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = renderMap; document.head.appendChild(script);
      } else { setTimeout(renderMap, 200); }
    }
    return () => { if (mapInstance.current) { mapInstance.current.remove(); mapInstance.current = null; } };
  }, [session]);

  const duration = session.started_at && session.ended_at
    ? Math.round((new Date(session.ended_at) - new Date(session.started_at)) / 60000) : null;
  const dist = calcDistance(session.gps_trail);
  const checkpoints = session.checkpoints_completed || [];
  const allMedia = [
    ...(session.media || []),
    ...checkpoints.flatMap(cp => [
      ...(cp.media || []),
      ...(cp.photo_url ? [{ url: cp.photo_url, type: 'image/jpeg', name: cp.name || 'photo' }] : []),
    ]),
  ];
  const [lightbox, setLightbox] = useState(null);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:'640px',maxHeight:'92vh',overflowY:'auto'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Patrol — {fmtDate(session.started_at)}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Meta strip */}
        <div style={{display:'flex',gap:'0.75rem',flexWrap:'wrap',marginBottom:'1rem',padding:'0.75rem',background:'var(--surface-2)',borderRadius:'6px'}}>
          {session.officer && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase',marginBottom:'2px'}}>Officer</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{session.officer.first_name} {session.officer.last_name}</div></div>}
          {session.site && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase',marginBottom:'2px'}}>Site</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{session.site.name}</div></div>}
          <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase',marginBottom:'2px'}}>Time</div><div style={{fontSize:'0.875rem'}}>{fmtTime(session.started_at)}{session.ended_at ? ` → ${fmtTime(session.ended_at)}` : ' (active)'}</div></div>
          {duration != null && <div><div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase',marginBottom:'2px'}}>Duration</div><div style={{fontWeight:600,fontSize:'0.875rem'}}>{duration >= 60 ? `${Math.floor(duration/60)}h ${duration%60}m` : `${duration}m`}</div></div>}
        </div>

        {/* Stats */}
        <div style={{display:'flex',gap:'0.75rem',marginBottom:'1rem'}}>
          <div style={{flex:1,padding:'0.625rem',background:'var(--surface-2)',borderRadius:'6px',textAlign:'center'}}>
            <div style={{fontSize:'1.25rem',fontWeight:700}}>{checkpoints.length}</div>
            <div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Checkpoints</div>
          </div>
          {dist != null && <div style={{flex:1,padding:'0.625rem',background:'var(--surface-2)',borderRadius:'6px',textAlign:'center'}}>
            <div style={{fontSize:'1.25rem',fontWeight:700}}>{dist >= 1000 ? `${(dist/1000).toFixed(1)}km` : `${dist}m`}</div>
            <div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>Distance</div>
          </div>}
          {session.gps_trail?.length > 0 && <div style={{flex:1,padding:'0.625rem',background:'var(--surface-2)',borderRadius:'6px',textAlign:'center'}}>
            <div style={{fontSize:'1.25rem',fontWeight:700}}>{session.gps_trail.length}</div>
            <div style={{fontSize:'0.6875rem',color:'var(--text-3)',textTransform:'uppercase'}}>GPS Points</div>
          </div>}
        </div>

        {/* Map */}
        <div ref={mapRef} style={{width:'100%',height:'320px',borderRadius:'8px',border:'1px solid var(--border)',marginBottom:'1rem'}} />

        {/* Checkpoints */}
        {checkpoints.length > 0 && (
          <div style={{marginBottom:'1rem'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Checkpoints</div>
            <div style={{display:'flex',flexDirection:'column',gap:'0.375rem'}}>
              {checkpoints.map((cp, i) => (
                <div key={i} style={{display:'flex',alignItems:'center',gap:'0.625rem',padding:'0.5rem 0.625rem',background:'var(--surface-2)',borderRadius:'6px'}}>
                  <div style={{width:22,height:22,borderRadius:'50%',background:'#a78bfa',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'10px',fontWeight:700,color:'#0b1222',flexShrink:0}}>{i+1}</div>
                  <div style={{flex:1,fontSize:'0.8125rem',fontWeight:500}}>{typeof cp === 'object' ? cp.name || `Checkpoint ${i+1}` : cp}</div>
                  {typeof cp === 'object' && cp.timestamp && <div style={{fontSize:'0.75rem',color:'var(--text-3)',flexShrink:0}}>{fmtTime(cp.timestamp)}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photos */}
        {allMedia.length > 0 && (
          <div style={{marginBottom:'1rem'}}>
            <div style={{fontSize:'0.75rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'0.5rem'}}>Photos ({allMedia.length})</div>
            <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
              {allMedia.map((m, i) => (
                <div key={i} onClick={() => m.type?.startsWith('image') && setLightbox(m.url)}
                  style={{width:80,height:80,borderRadius:'8px',overflow:'hidden',border:'1px solid var(--border)',cursor:'zoom-in',flexShrink:0}}>
                  <img src={m.url} style={{width:'100%',height:'100%',objectFit:'cover'}} alt={m.name||'photo'} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.92)',zIndex:10000,display:'flex',alignItems:'center',justifyContent:'center',padding:'1rem'}}>
          <img src={lightbox} style={{maxWidth:'100%',maxHeight:'90vh',borderRadius:'8px',objectFit:'contain'}} />
          <button onClick={() => setLightbox(null)} style={{position:'absolute',top:'1rem',right:'1rem',background:'rgba(255,255,255,0.15)',border:'none',color:'#fff',width:36,height:36,borderRadius:'50%',fontSize:'1.25rem',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>×</button>
        </div>
      )}
    </div>
  );
}

// ── Manager Patrol History Screen ────────────────────────────────────────────
function PatrolHistoryScreen({ user }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [siteFilter, setSiteFilter] = useState('');
  const [sites, setSites] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const [sessRes, sitesRes] = await Promise.all([
          api.patrols.listSessions({ limit: 100 }),
          api.sites.list(),
        ]);
        setSessions(sessRes.data || []);
        setSites(sitesRes.data || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  async function openSession(s) {
    if (s.gps_trail) { setSelected(s); return; }
    setLoadingSession(true);
    try {
      const res = await api.patrols.getSession(s.id);
      setSelected(res.data);
    } catch (err) { alert('Failed to load patrol: ' + err.message); }
    finally { setLoadingSession(false); }
  }

  const filtered = siteFilter ? sessions.filter(s => s.site_id === siteFilter) : sessions;

  const fmtDate = t => t ? new Date(t).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric', timeZone:'Europe/London' }) : '—';
  const fmtTime = t => t ? new Date(t).toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit', timeZone:'Europe/London' }) : '—';
  const duration = s => s.started_at && s.ended_at ? Math.round((new Date(s.ended_at) - new Date(s.started_at)) / 60000) : null;
  const fmtDur = d => d == null ? '—' : d >= 60 ? `${Math.floor(d/60)}h ${d%60}m` : `${d}m`;

  return (
    <div style={{padding:'1.5rem'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1.25rem',flexWrap:'wrap',gap:'0.75rem'}}>
        <div>
          <h2 style={{fontSize:'1.125rem',fontWeight:700,margin:0}}>Patrol History</h2>
          <p style={{fontSize:'0.8125rem',color:'var(--text-2)',margin:'0.25rem 0 0'}}>All completed patrols — click to view route map</p>
        </div>
        <select value={siteFilter} onChange={e => setSiteFilter(e.target.value)}
          style={{padding:'0.5rem 0.75rem',background:'var(--surface-2)',border:'1px solid var(--border)',borderRadius:'6px',color:'var(--text)',fontSize:'0.875rem'}}>
          <option value="">All Sites</option>
          {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div style={{textAlign:'center',padding:'3rem',color:'var(--text-3)'}}>No patrols found</div>
      ) : (
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)'}}>
                {['Date','Officer','Site','Start','End','Duration','Checkpoints','GPS Points',''].map(h => (
                  <th key={h} style={{padding:'0.625rem 0.75rem',textAlign:'left',fontSize:'0.75rem',fontWeight:600,color:'var(--text-2)',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => (
                <tr key={s.id} onClick={() => openSession(s)}
                  style={{borderBottom:'1px solid var(--border)',cursor:'pointer'}}
                  onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  <td style={{padding:'0.625rem 0.75rem',fontSize:'0.8125rem',whiteSpace:'nowrap'}}>{fmtDate(s.started_at)}</td>
                  <td style={{padding:'0.625rem 0.75rem',fontSize:'0.8125rem'}}>{s.officer ? `${s.officer.first_name} ${s.officer.last_name}` : '—'}</td>
                  <td style={{padding:'0.625rem 0.75rem',fontSize:'0.8125rem'}}>{s.site?.name || '—'}</td>
                  <td style={{padding:'0.625rem 0.75rem',fontSize:'0.8125rem',whiteSpace:'nowrap'}}>{fmtTime(s.started_at)}</td>
                  <td style={{padding:'0.625rem 0.75rem',fontSize:'0.8125rem',whiteSpace:'nowrap'}}>{fmtTime(s.ended_at)}</td>
                  <td style={{padding:'0.625rem 0.75rem',fontSize:'0.8125rem',fontWeight:600}}>{fmtDur(duration(s))}</td>
                  <td style={{padding:'0.625rem 0.75rem',fontSize:'0.8125rem',textAlign:'center'}}>{(s.checkpoints_completed?.length) || 0}</td>
                  <td style={{padding:'0.625rem 0.75rem',fontSize:'0.8125rem',textAlign:'center'}}>{s.gps_trail?.length || 0}</td>
                  <td style={{padding:'0.625rem 0.75rem',fontSize:'0.75rem',color:'var(--blue)',whiteSpace:'nowrap'}}>View map →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loadingSession && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div className="spinner" />
        </div>
      )}

      {selected && <PatrolSessionModal session={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ── Site Assignment Instructions Editor ───────────────────────────────────────
function SiteAIEditor({ siteId, siteName }) {
  const [ai, setAI] = useState(null);
  const [sections, setSections] = useState([]);
  const [title, setTitle] = useState('Assignment Instructions');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [revisions, setRevisions] = useState([]);
  const [showRevisions, setShowRevisions] = useState(false);
  const [viewingRev, setViewingRev] = useState(null);
  const [declarations, setDeclarations] = useState(null);
  const [showDeclarations, setShowDeclarations] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.siteAI.get(siteId),
      api.siteAI.revisions(siteId),
    ]).then(([aiRes, revRes]) => {
      const d = aiRes.data;
      if (d) {
        setAI(d);
        setTitle(d.title || 'Assignment Instructions');
        setSections(d.sections || []);
      }
      setRevisions(revRes.data || []);
    }).catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [siteId]);

  function addSection() {
    setSections(s => [...s, { title: '', content: '' }]);
  }
  function updateSection(i, field, val) {
    setSections(s => s.map((sec, j) => j === i ? { ...sec, [field]: val } : sec));
  }
  function removeSection(i) {
    setSections(s => s.filter((_, j) => j !== i));
  }
  function moveSection(i, dir) {
    setSections(s => {
      const arr = [...s];
      const target = i + dir;
      if (target < 0 || target >= arr.length) return arr;
      [arr[i], arr[target]] = [arr[target], arr[i]];
      return arr;
    });
  }

  async function save() {
    try {
      setSaving(true); setError(null);
      const res = await api.siteAI.save(siteId, { title, sections });
      setAI(res.data);
      setSuccess('Draft saved');
      setTimeout(() => setSuccess(null), 2000);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  }

  async function publish() {
    if (!confirm(`Publish Revision ${(ai?.revision || 0) + 1}? Officers will need to re-declare.`)) return;
    try {
      setPublishing(true); setError(null);
      // Save first to ensure latest content is persisted
      await api.siteAI.save(siteId, { title, sections });
      const res = await api.siteAI.publish(siteId);
      setAI(res.data);
      setSuccess(`Published as Revision ${res.revision}`);
      // Refresh revisions
      const revRes = await api.siteAI.revisions(siteId);
      setRevisions(revRes.data || []);
      setTimeout(() => setSuccess(null), 3000);
    } catch (e) { setError(e.message); }
    finally { setPublishing(false); }
  }

  async function viewRevision(rev) {
    try {
      const res = await api.siteAI.revision(siteId, rev);
      setViewingRev(res.data);
    } catch (e) { setError(e.message); }
  }

  async function loadDeclarations() {
    setShowDeclarations(!showDeclarations);
    if (!showDeclarations) {
      try {
        const res = await api.siteAI.declarations(siteId);
        setDeclarations(res);
      } catch (e) { setError(e.message); }
    }
  }

  async function downloadPdf(revision) {
    try {
      setPdfLoading(true);
      const res = await api.siteAI.pdf(siteId, revision);
      const blob = new Blob([Uint8Array.from(atob(res.pdf), c => c.charCodeAt(0))], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = res.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e.message); }
    finally { setPdfLoading(false); }
  }

  if (loading) return <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>;

  return (
    <div>
      {/* Header bar */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem',flexWrap:'wrap',gap:'0.5rem'}}>
        <div style={{display:'flex',alignItems:'center',gap:'0.75rem'}}>
          <div style={{fontWeight:700,fontSize:'1rem'}}>Assignment Instructions</div>
          {ai?.revision > 0 && (
            <span className="badge badge-info" style={{fontSize:'0.6875rem'}}>Rev {ai.revision} — {ai.status === 'published' ? 'Published' : 'Draft'}</span>
          )}
          {ai?.revision === 0 && <span className="badge badge-neutral" style={{fontSize:'0.6875rem'}}>New — Unpublished</span>}
          {ai?.revision > 0 && ai?.client_approved && (
            <span style={{fontSize:'0.6875rem',padding:'2px 8px',borderRadius:'999px',fontWeight:600,background:'rgba(16,185,129,0.1)',color:'#059669',border:'1px solid rgba(16,185,129,0.3)'}}>
              Client: {ai.client_approved_by} — {new Date(ai.client_approved_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'})}
            </span>
          )}
          {ai?.revision > 0 && ai?.status === 'published' && !ai?.client_approved && (
            <span style={{fontSize:'0.6875rem',padding:'2px 8px',borderRadius:'999px',fontWeight:600,background:'rgba(245,158,11,0.1)',color:'#d97706',border:'1px solid rgba(245,158,11,0.3)'}}>
              Client approval pending
            </span>
          )}
        </div>
        <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
          {ai?.revision > 0 && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={loadDeclarations}>
                {showDeclarations ? 'Hide' : 'Declarations'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowRevisions(!showRevisions)}>
                {showRevisions ? 'Hide History' : `History (${revisions.length})`}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => downloadPdf(ai.revision)} disabled={pdfLoading}>
                {pdfLoading ? 'Generating…' : 'Download PDF'}
              </button>
            </>
          )}
          <button className="btn btn-ghost btn-sm" onClick={addSection}>+ Section</button>
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Draft'}</button>
          <button className="btn btn-sm" style={{background:'#16a34a',color:'#fff',border:'none',fontWeight:600}} onClick={publish} disabled={publishing}>
            {publishing ? 'Publishing…' : `Publish Rev ${(ai?.revision || 0) + 1}`}
          </button>
        </div>
      </div>

      {success && <div className="alert alert-success" style={{marginBottom:'1rem'}}>{success}</div>}
      {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}

      {/* Declarations panel */}
      {showDeclarations && declarations && (
        <div className="card" style={{marginBottom:'1rem',padding:'1rem'}}>
          <div style={{fontWeight:700,fontSize:'0.875rem',marginBottom:'0.75rem'}}>
            Declarations — Revision {declarations.revision}
          </div>
          {(declarations.data || []).length === 0 ? (
            <div style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>No officers assigned to this site.</div>
          ) : (
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.8125rem'}}>
              <thead>
                <tr style={{borderBottom:'1px solid var(--border)'}}>
                  <th style={{textAlign:'left',padding:'0.375rem 0.5rem',fontWeight:600,color:'var(--text-3)',fontSize:'0.6875rem'}}>Officer</th>
                  <th style={{textAlign:'center',padding:'0.375rem 0.5rem',fontWeight:600,color:'var(--text-3)',fontSize:'0.6875rem'}}>Status</th>
                  <th style={{textAlign:'right',padding:'0.375rem 0.5rem',fontWeight:600,color:'var(--text-3)',fontSize:'0.6875rem'}}>Date</th>
                </tr>
              </thead>
              <tbody>
                {(declarations.data || []).map(o => (
                  <tr key={o.id} style={{borderBottom:'1px solid var(--border)'}}>
                    <td style={{padding:'0.375rem 0.5rem',fontWeight:500}}>{o.first_name} {o.last_name}</td>
                    <td style={{padding:'0.375rem 0.5rem',textAlign:'center'}}>
                      {o.declared
                        ? <span style={{color:'#16a34a',fontWeight:600}}>✓ Declared</span>
                        : <span style={{color:'#ef4444',fontWeight:600}}>Outstanding</span>}
                    </td>
                    <td style={{padding:'0.375rem 0.5rem',textAlign:'right',color:'var(--text-3)',fontSize:'0.75rem'}}>
                      {o.declared_at ? new Date(o.declared_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Revision history panel */}
      {showRevisions && (
        <div className="card" style={{marginBottom:'1rem',padding:'1rem'}}>
          <div style={{fontWeight:700,fontSize:'0.875rem',marginBottom:'0.75rem'}}>Revision History</div>
          {revisions.length === 0 ? (
            <div style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>No published revisions yet.</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:'0.375rem'}}>
              {revisions.map(r => (
                <div key={r.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'0.5rem 0.75rem',background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'6px',fontSize:'0.8125rem'}}>
                  <div>
                    <span style={{fontWeight:700,color:'var(--blue)',marginRight:'0.5rem'}}>Rev {r.revision}</span>
                    <span>{r.title}</span>
                    <span style={{color:'var(--text-3)',fontSize:'0.6875rem',marginLeft:'0.5rem'}}>
                      by {r.publisher?.first_name} {r.publisher?.last_name}
                    </span>
                  </div>
                  <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
                    <span style={{fontSize:'0.6875rem',color:'var(--text-3)'}}>
                      {new Date(r.published_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' })}
                    </span>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:'0.6875rem'}} onClick={() => viewRevision(r.revision)}>View</button>
                    <button className="btn btn-ghost btn-sm" style={{fontSize:'0.6875rem'}} onClick={() => downloadPdf(r.revision)}>PDF</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Viewing old revision */}
      {viewingRev && (
        <div className="card" style={{marginBottom:'1rem',padding:'1rem',border:'2px solid var(--blue)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
            <div style={{fontWeight:700,fontSize:'0.875rem',color:'var(--blue)'}}>
              Viewing Revision {viewingRev.revision} — {viewingRev.title}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setViewingRev(null)}>Close</button>
          </div>
          <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginBottom:'1rem'}}>
            Published {new Date(viewingRev.published_at).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}
          </div>
          {(viewingRev.sections || []).map((sec, i) => (
            <div key={i} style={{marginBottom:'1rem'}}>
              <div style={{fontWeight:700,fontSize:'0.875rem',color:'var(--text)',marginBottom:'0.25rem'}}>{i + 1}. {sec.title}</div>
              <div style={{fontSize:'0.8125rem',color:'var(--text-2)',whiteSpace:'pre-line',lineHeight:1.6}}>{sec.content}</div>
            </div>
          ))}
        </div>
      )}

      {/* Title */}
      <div className="card" style={{marginBottom:'0.75rem',padding:'0.875rem 1rem'}}>
        <label style={{fontSize:'0.6875rem',fontWeight:700,color:'var(--text-3)',textTransform:'uppercase',letterSpacing:'0.05em',marginBottom:'0.25rem',display:'block'}}>Document Title</label>
        <input className="input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Assignment Instructions" style={{fontWeight:600,fontSize:'1rem'}} />
      </div>

      {/* Sections editor */}
      {sections.length === 0 ? (
        <div className="empty-state">
          <p>No sections yet. Add the first section to start building the Assignment Instructions for {siteName}.</p>
          <button className="btn btn-primary" style={{marginTop:'1rem'}} onClick={addSection}>Add First Section</button>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:'0.75rem'}}>
          {sections.map((sec, i) => (
            <div key={i} className="card" style={{padding:'1rem'}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.5rem'}}>
                <div style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                  <span style={{fontSize:'0.75rem',fontWeight:800,color:'var(--blue)',minWidth:'1.5rem'}}>{i + 1}.</span>
                  <input className="input" value={sec.title} onChange={e => updateSection(i, 'title', e.target.value)}
                    placeholder="Section title (e.g. Site Threats, Emergency Procedures)" style={{fontWeight:600,flex:1}} />
                </div>
                <div style={{display:'flex',gap:'0.25rem',marginLeft:'0.5rem'}}>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:'0.6875rem',padding:'0.25rem 0.5rem'}} onClick={() => moveSection(i, -1)} disabled={i === 0}>↑</button>
                  <button className="btn btn-ghost btn-sm" style={{fontSize:'0.6875rem',padding:'0.25rem 0.5rem'}} onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1}>↓</button>
                  <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)',fontSize:'0.6875rem'}} onClick={() => removeSection(i)}>Remove</button>
                </div>
              </div>
              <textarea className="input" rows={8} value={sec.content} onChange={e => updateSection(i, 'content', e.target.value)}
                placeholder="Section content — plain text. Use line breaks for structure." style={{fontSize:'0.8125rem',lineHeight:1.6}} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export { PatrolHistoryScreen };
export { PatrolSessionModal };

function ExpectedVisitorsScreen({ user }) {
  const [bookings, setBookings] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [siteFilter, setSiteFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  async function load() {
    try {
      const [sitesRes, expectedRes] = await Promise.all([
        api.sites.list(),
        api.expectedVisitors.list({ upcoming: 'true' }),
      ]);
      setSites(sitesRes.data || []);
      const rows = expectedRes.data || [];
      const groups = {};
      rows.forEach(r => {
        const gid = r.booking_group_id;
        if (!gid) return;
        if (!groups[gid]) {
          groups[gid] = {
            booking_group_id: gid, visitor_name: r.visitor_name, company_name: r.company_name,
            who_visiting: r.who_visiting, site_id: r.site_id, site_name: r.site?.name || '—',
            personnel_count: r.personnel_count, vehicle_reg: r.vehicle_reg,
            expected_time: r.expected_time, notes: r.notes, dates: [],
          };
        }
        groups[gid].dates.push(r.expected_date);
      });
      const grouped = Object.values(groups).map(g => {
        g.dates.sort();
        g.date_from = g.dates[0];
        g.date_to = g.dates[g.dates.length - 1];
        g.day_count = g.dates.length;
        return g;
      });
      grouped.sort((a, b) => a.date_from.localeCompare(b.date_from));
      setBookings(grouped);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function cancelBooking(groupId) {
    setCancelling(true);
    try {
      await api.expectedVisitors.cancelGroup(groupId);
      setCancelConfirm(null);
      load();
    } catch (err) { alert(err.message); }
    finally { setCancelling(false); }
  }

  const filtered = siteFilter ? bookings.filter(b => b.site_id === siteFilter) : bookings;
  const fmtDate = d => d ? new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : '—';

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Expected Visitors</div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          <PlusIcon style={{width:'0.875rem',height:'0.875rem'}} /> Add Expected Visit
        </button>
      </div>
      <div className="page-content">
        <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'1rem',flexWrap:'wrap'}}>
          <select className="input" style={{width:'180px'}} value={siteFilter} onChange={e => setSiteFilter(e.target.value)}>
            <option value="">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <span style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>{filtered.length} booking{filtered.length!==1?'s':''}</span>
        </div>
        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><p>No upcoming expected visitors</p></div>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Visitor / Company</th><th>Purpose</th><th>Site</th><th>Dates</th><th>ETA</th><th>Personnel</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(b => (
                <tr key={b.booking_group_id}>
                  <td>
                    <div style={{fontWeight:500}}>{b.visitor_name}</div>
                    {b.company_name && <div style={{fontSize:'0.75rem',color:'var(--text-2)'}}>{b.company_name}</div>}
                  </td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{b.who_visiting || '—'}</td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{b.site_name}</td>
                  <td style={{fontSize:'0.8125rem',whiteSpace:'nowrap'}}>
                    {b.date_from === b.date_to ? fmtDate(b.date_from) : `${fmtDate(b.date_from)} – ${fmtDate(b.date_to)}`}
                    {b.day_count > 1 && <span style={{fontSize:'0.6875rem',color:'var(--text-3)',marginLeft:'0.375rem'}}>({b.day_count}d)</span>}
                  </td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{b.expected_time ? b.expected_time.slice(0,5) : '—'}</td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{b.personnel_count || 1}</td>
                  <td style={{textAlign:'right',display:'flex',gap:'0.375rem',justifyContent:'flex-end'}}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditBooking(b)}>Edit</button>
                    <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={() => setCancelConfirm(b)}>Cancel</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate && <ExpectedVisitorCreateModal sites={sites} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editBooking && <ExpectedVisitorEditModal booking={editBooking} onClose={() => setEditBooking(null)} onSaved={() => { setEditBooking(null); load(); }} />}
      {cancelConfirm && (
        <div className="modal-overlay" onClick={() => setCancelConfirm(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">Cancel Expected Visit</div>
              <button className="modal-close" onClick={() => setCancelConfirm(null)}>×</button>
            </div>
            <p style={{fontSize:'0.875rem',color:'var(--text-2)',margin:'0 0 1rem'}}>
              Cancel this expected visit? Future days will be removed; any already-arrived days are kept.
            </p>
            <div style={{fontWeight:500,marginBottom:'1rem'}}>{cancelConfirm.visitor_name} — {cancelConfirm.site_name}</div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setCancelConfirm(null)}>Keep</button>
              <button className="btn btn-primary" style={{background:'var(--danger)',borderColor:'var(--danger)'}} onClick={() => cancelBooking(cancelConfirm.booking_group_id)} disabled={cancelling}>
                {cancelling ? 'Cancelling...' : 'Cancel Visit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ExpectedVisitorCreateModal({ sites, onClose, onSaved }) {
  const [form, setForm] = useState({
    visitor_name: '', company_name: '', who_visiting: '', site_id: '',
    expected_from: '', expected_to: '', expected_time: '',
    personnel_count: '1', vehicle_reg: '', notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!form.site_id) { setError('Please select a site'); return; }
    if (!form.visitor_name.trim()) { setError('Visitor name is required'); return; }
    if (!form.expected_from || !form.expected_to) { setError('Start and end dates are required'); return; }
    setSaving(true);
    try {
      await api.expectedVisitors.create({
        site_id: form.site_id,
        visitor_name: form.visitor_name.trim(),
        company_name: form.company_name.trim() || null,
        who_visiting: form.who_visiting.trim() || null,
        expected_from: form.expected_from,
        expected_to: form.expected_to,
        expected_time: form.expected_time || null,
        personnel_count: parseInt(form.personnel_count) || 1,
        vehicle_reg: form.vehicle_reg.trim() || null,
        notes: form.notes.trim() || null,
      });
      onSaved();
    } catch (err) { setError(err.message || err.error || 'Failed to create'); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Add Expected Visit</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        <div className="field">
          <label className="label">Site</label>
          <select className="input" value={form.site_id} onChange={e => setForm(f=>({...f,site_id:e.target.value}))}>
            <option value="">Select site...</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="field">
          <label className="label">Visitor / Company Name</label>
          <input className="input" value={form.visitor_name} onChange={e => setForm(f=>({...f,visitor_name:e.target.value}))} placeholder="e.g. Severn Trent, BT Engineer" />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field">
            <label className="label">Company</label>
            <input className="input" value={form.company_name} onChange={e => setForm(f=>({...f,company_name:e.target.value}))} placeholder="Company name (optional)" />
          </div>
          <div className="field">
            <label className="label">Purpose</label>
            <input className="input" value={form.who_visiting} onChange={e => setForm(f=>({...f,who_visiting:e.target.value}))} placeholder="e.g. Meter reading, Lift repair" />
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0.75rem'}}>
          <div className="field">
            <label className="label">From</label>
            <input type="date" className="input" value={form.expected_from} onChange={e => setForm(f=>({...f,expected_from:e.target.value}))} />
          </div>
          <div className="field">
            <label className="label">To</label>
            <input type="date" className="input" value={form.expected_to} onChange={e => setForm(f=>({...f,expected_to:e.target.value}))} />
          </div>
          <div className="field">
            <label className="label">ETA (optional)</label>
            <input type="time" className="input" value={form.expected_time} onChange={e => setForm(f=>({...f,expected_time:e.target.value}))} />
          </div>
        </div>
        <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginBottom:'0.75rem'}}>A multi-day range creates one entry per day for the officer's daily expected list.</div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field">
            <label className="label">Personnel</label>
            <input type="number" className="input" min="1" value={form.personnel_count} onChange={e => setForm(f=>({...f,personnel_count:e.target.value}))} />
          </div>
          <div className="field">
            <label className="label">Vehicle Reg</label>
            <input className="input" value={form.vehicle_reg} onChange={e => setForm(f=>({...f,vehicle_reg:e.target.value}))} placeholder="Optional" />
          </div>
        </div>
        <div className="field">
          <label className="label">Notes</label>
          <textarea className="input" rows={2} value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} placeholder="Optional notes for the officer" />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Add Expected Visit'}</button>
        </div>
      </div>
    </div>
  );
}

function ExpectedVisitorEditModal({ booking, onClose, onSaved }) {
  const [form, setForm] = useState({
    visitor_name: booking.visitor_name || '', company_name: booking.company_name || '',
    who_visiting: booking.who_visiting || '', expected_time: booking.expected_time ? booking.expected_time.slice(0,5) : '',
    personnel_count: String(booking.personnel_count || 1), vehicle_reg: booking.vehicle_reg || '', notes: booking.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!form.visitor_name.trim()) { setError('Visitor name is required'); return; }
    setSaving(true);
    try {
      await api.expectedVisitors.updateGroup(booking.booking_group_id, {
        visitor_name: form.visitor_name.trim(),
        company_name: form.company_name.trim() || null,
        who_visiting: form.who_visiting.trim() || null,
        expected_time: form.expected_time || null,
        personnel_count: parseInt(form.personnel_count) || 1,
        vehicle_reg: form.vehicle_reg.trim() || null,
        notes: form.notes.trim() || null,
      });
      onSaved();
    } catch (err) { setError(err.message || 'Failed to update'); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Edit Expected Visit</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        <div className="field">
          <label className="label">Visitor / Company Name</label>
          <input className="input" value={form.visitor_name} onChange={e => setForm(f=>({...f,visitor_name:e.target.value}))} />
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field">
            <label className="label">Company</label>
            <input className="input" value={form.company_name} onChange={e => setForm(f=>({...f,company_name:e.target.value}))} />
          </div>
          <div className="field">
            <label className="label">Purpose</label>
            <input className="input" value={form.who_visiting} onChange={e => setForm(f=>({...f,who_visiting:e.target.value}))} />
          </div>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0.75rem'}}>
          <div className="field">
            <label className="label">ETA</label>
            <input type="time" className="input" value={form.expected_time} onChange={e => setForm(f=>({...f,expected_time:e.target.value}))} />
          </div>
          <div className="field">
            <label className="label">Personnel</label>
            <input type="number" className="input" min="1" value={form.personnel_count} onChange={e => setForm(f=>({...f,personnel_count:e.target.value}))} />
          </div>
          <div className="field">
            <label className="label">Vehicle Reg</label>
            <input className="input" value={form.vehicle_reg} onChange={e => setForm(f=>({...f,vehicle_reg:e.target.value}))} />
          </div>
        </div>
        <div className="field">
          <label className="label">Notes</label>
          <textarea className="input" rows={2} value={form.notes} onChange={e => setForm(f=>({...f,notes:e.target.value}))} />
        </div>
        <div style={{fontSize:'0.75rem',color:'var(--text-3)',marginBottom:'0.75rem'}}>Date range cannot be changed. To adjust dates, cancel and re-create the booking.</div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

export { ExpectedVisitorsScreen };

function TenantDirectoryScreen({ user }) {
  const [tenants, setTenants] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [unitFilter, setUnitFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editTenant, setEditTenant] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  async function load() {
    try {
      const [sitesRes, tenantsRes] = await Promise.all([api.sites.list(), api.tenants.list()]);
      setSites(sitesRes.data || []);
      setTenants(tenantsRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function deleteTenant(id) {
    try { await api.tenants.delete(id); setDeleteConfirmId(null); load(); }
    catch (e) { alert(e.message); }
  }

  let filtered = tenants;
  if (siteFilter) filtered = filtered.filter(t => t.site_id === siteFilter);
  if (unitFilter) filtered = filtered.filter(t => t.unit_ref === unitFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(t =>
      (t.unit_ref || '').toLowerCase().includes(q) ||
      (t.tenant_name || '').toLowerCase().includes(q) ||
      (t.contacts || []).some(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q)
      )
    );
  }

  const unitOptions = [...new Set(
    tenants.filter(t => !siteFilter || t.site_id === siteFilter).map(t => t.unit_ref).filter(Boolean)
  )].sort();

  const siteMap = {};
  sites.forEach(s => { siteMap[s.id] = s.name; });

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Tenant Directory</div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          <PlusIcon style={{width:'0.875rem',height:'0.875rem'}} /> Add Tenant
        </button>
      </div>
      <div className="page-content">
        <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'1rem',flexWrap:'wrap'}}>
          <input className="input" style={{width:'220px'}} placeholder="Search name, unit, contact..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="input" style={{width:'180px'}} value={siteFilter} onChange={e => { setSiteFilter(e.target.value); setUnitFilter(''); }}>
            <option value="">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          {unitOptions.length > 0 && (
            <select className="input" style={{width:'140px'}} value={unitFilter} onChange={e => setUnitFilter(e.target.value)}>
              <option value="">All Units</option>
              {unitOptions.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          )}
          <span style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>{filtered.length} tenant{filtered.length!==1?'s':''}</span>
        </div>
        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><p>No tenants found</p></div>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Unit</th><th>Tenant</th><th>Site</th><th>Status</th><th>Contacts</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(t => {
                const contacts = (t.contacts || []).sort((a, b) => (a.position || 99) - (b.position || 99));
                return (
                  <tr key={t.id}>
                    <td style={{fontFamily:'monospace',fontSize:'0.8125rem',fontWeight:600}}>{t.unit_ref || '—'}</td>
                    <td style={{fontWeight:500}}>{t.tenant_name}</td>
                    <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{siteMap[t.site_id] || '—'}</td>
                    <td><span className={`badge ${t.status==='active'?'badge-success':t.status==='vacant'?'badge-warning':'badge-neutral'}`}>{t.status || 'active'}</span></td>
                    <td style={{fontSize:'0.75rem',color:'var(--text-2)',maxWidth:'300px'}}>
                      {contacts.length === 0 ? <span style={{color:'var(--text-3)'}}>No contacts</span> : contacts.map((c, i) => (
                        <span key={c.id}>{i > 0 && ' · '}{c.position}. {c.name || '—'}{c.phone ? ` ${c.phone}` : ''}</span>
                      ))}
                    </td>
                    <td style={{textAlign:'right',display:'flex',gap:'0.375rem',justifyContent:'flex-end'}}>
                      <button className="btn btn-ghost btn-sm" onClick={() => setEditTenant(t)}>Edit</button>
                      {deleteConfirmId === t.id ? (
                        <>
                          <button className="btn btn-sm" style={{background:'var(--danger)',color:'#fff',border:'none'}} onClick={() => deleteTenant(t.id)}>Yes</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setDeleteConfirmId(null)}>No</button>
                        </>
                      ) : (
                        <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={() => setDeleteConfirmId(t.id)}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {showCreate && <TenantFormModal sites={sites} onClose={() => setShowCreate(false)} onSaved={(newTenant) => { setShowCreate(false); if (newTenant) setEditTenant(newTenant); else load(); }} />}
      {editTenant && <TenantEditModal tenant={editTenant} sites={sites} onClose={() => setEditTenant(null)} onSaved={() => { setEditTenant(null); load(); }} />}
    </div>
  );
}

function TenantFormModal({ sites, onClose, onSaved }) {
  const [form, setForm] = useState({ site_id: '', unit_ref: '', tenant_name: '', status: 'active', comments: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!form.site_id) { setError('Please select a site'); return; }
    if (!form.tenant_name.trim()) { setError('Tenant name is required'); return; }
    setSaving(true);
    try {
      const res = await api.tenants.create({
        site_id: form.site_id,
        unit_ref: form.unit_ref.trim() || null,
        tenant_name: form.tenant_name.trim(),
        status: form.status,
        comments: form.comments.trim() || null,
      });
      onSaved(res.data);
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Add Tenant</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        <div className="field">
          <label className="label">Site</label>
          <select className="input" value={form.site_id} onChange={e => setForm(f=>({...f,site_id:e.target.value}))}>
            <option value="">Select site...</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field">
            <label className="label">Unit Ref</label>
            <input className="input" value={form.unit_ref} onChange={e => setForm(f=>({...f,unit_ref:e.target.value}))} placeholder="e.g. Unit 12, G.03" />
          </div>
          <div className="field">
            <label className="label">Status</label>
            <select className="input" value={form.status} onChange={e => setForm(f=>({...f,status:e.target.value}))}>
              <option value="active">Active</option>
              <option value="vacant">Vacant</option>
              <option value="void">Void</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label className="label">Tenant Name</label>
          <input className="input" value={form.tenant_name} onChange={e => setForm(f=>({...f,tenant_name:e.target.value}))} placeholder="Company or person name" />
        </div>
        <div className="field">
          <label className="label">Comments</label>
          <textarea className="input" rows={2} value={form.comments} onChange={e => setForm(f=>({...f,comments:e.target.value}))} placeholder="Optional notes" />
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Add Tenant'}</button>
        </div>
      </div>
    </div>
  );
}

function TenantEditModal({ tenant, sites, onClose, onSaved }) {
  const [form, setForm] = useState({
    unit_ref: tenant.unit_ref || '', tenant_name: tenant.tenant_name || '',
    status: tenant.status || 'active', comments: tenant.comments || '',
  });
  const [contacts, setContacts] = useState((tenant.contacts || []).sort((a, b) => (a.position || 99) - (b.position || 99)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [contactSaving, setContactSaving] = useState(null);

  async function saveTenant() {
    if (!form.tenant_name.trim()) { setError('Tenant name is required'); return; }
    setSaving(true);
    try {
      await api.tenants.update(tenant.id, {
        unit_ref: form.unit_ref.trim() || null,
        tenant_name: form.tenant_name.trim(),
        status: form.status,
        comments: form.comments.trim() || null,
      });
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  async function addContact() {
    setContactSaving('new');
    try {
      const res = await api.tenants.addContact(tenant.id, { position: contacts.length + 1, name: '', phone: '', email: '' });
      setContacts(prev => [...prev, res.data].sort((a, b) => (a.position || 99) - (b.position || 99)));
    } catch (err) { setError(err.message); }
    finally { setContactSaving(null); }
  }

  async function saveContact(c) {
    setContactSaving(c.id);
    try {
      await api.tenants.updateContact(c.id, { position: c.position, name: c.name, phone: c.phone, email: c.email, label: c.label, notes: c.notes });
    } catch (err) { setError(err.message); }
    finally { setContactSaving(null); }
  }

  async function deleteContact(contactId) {
    try {
      await api.tenants.deleteContact(contactId);
      setContacts(prev => prev.filter(c => c.id !== contactId));
    } catch (err) { setError(err.message); }
  }

  function updateContactField(contactId, field, value) {
    setContacts(prev => prev.map(c => c.id === contactId ? { ...c, [field]: value } : c));
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:'600px'}} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Edit Tenant — {tenant.tenant_name}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field">
            <label className="label">Unit Ref</label>
            <input className="input" value={form.unit_ref} onChange={e => setForm(f=>({...f,unit_ref:e.target.value}))} />
          </div>
          <div className="field">
            <label className="label">Status</label>
            <select className="input" value={form.status} onChange={e => setForm(f=>({...f,status:e.target.value}))}>
              <option value="active">Active</option>
              <option value="vacant">Vacant</option>
              <option value="void">Void</option>
            </select>
          </div>
        </div>
        <div className="field">
          <label className="label">Tenant Name</label>
          <input className="input" value={form.tenant_name} onChange={e => setForm(f=>({...f,tenant_name:e.target.value}))} />
        </div>
        <div className="field">
          <label className="label">Comments</label>
          <textarea className="input" rows={2} value={form.comments} onChange={e => setForm(f=>({...f,comments:e.target.value}))} />
        </div>
        <div style={{display:'flex',justifyContent:'flex-end',marginBottom:'1rem'}}>
          <button className="btn btn-primary btn-sm" onClick={saveTenant} disabled={saving}>{saving ? 'Saving...' : 'Save Tenant'}</button>
        </div>

        {/* Contacts */}
        <div style={{borderTop:'1px solid var(--border)',paddingTop:'1rem'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'0.75rem'}}>
            <div className="section-title" style={{margin:0,fontSize:'0.875rem'}}>Escalation Contacts</div>
            <button className="btn btn-ghost btn-sm" onClick={addContact} disabled={contactSaving === 'new'}>{contactSaving === 'new' ? 'Adding...' : '+ Add Contact'}</button>
          </div>
          {contacts.length === 0 ? (
            <div style={{fontSize:'0.8125rem',color:'var(--text-3)',padding:'0.5rem 0'}}>No contacts yet. Add an escalation contact above.</div>
          ) : (
            <div style={{display:'flex',flexDirection:'column',gap:'0.625rem'}}>
              {contacts.map(c => (
                <div key={c.id} style={{padding:'0.75rem',background:'var(--surface-2)',borderRadius:'var(--radius)',border:'1px solid var(--border)'}}>
                  <div style={{display:'grid',gridTemplateColumns:'60px 1fr 1fr',gap:'0.5rem',marginBottom:'0.5rem'}}>
                    <div className="field" style={{marginBottom:0}}>
                      <label className="label" style={{fontSize:'0.625rem'}}>Pos</label>
                      <input className="input" type="number" min="1" value={c.position || ''} onChange={e => updateContactField(c.id, 'position', parseInt(e.target.value) || 1)} style={{fontSize:'0.8125rem'}} />
                    </div>
                    <div className="field" style={{marginBottom:0}}>
                      <label className="label" style={{fontSize:'0.625rem'}}>Name</label>
                      <input className="input" value={c.name || ''} onChange={e => updateContactField(c.id, 'name', e.target.value)} style={{fontSize:'0.8125rem'}} />
                    </div>
                    <div className="field" style={{marginBottom:0}}>
                      <label className="label" style={{fontSize:'0.625rem'}}>Phone</label>
                      <input className="input" value={c.phone || ''} onChange={e => updateContactField(c.id, 'phone', e.target.value)} style={{fontSize:'0.8125rem'}} />
                    </div>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'0.5rem'}}>
                    <div className="field" style={{marginBottom:0}}>
                      <label className="label" style={{fontSize:'0.625rem'}}>Email</label>
                      <input className="input" value={c.email || ''} onChange={e => updateContactField(c.id, 'email', e.target.value)} style={{fontSize:'0.8125rem'}} />
                    </div>
                    <div className="field" style={{marginBottom:0}}>
                      <label className="label" style={{fontSize:'0.625rem'}}>Label</label>
                      <input className="input" value={c.label || ''} onChange={e => updateContactField(c.id, 'label', e.target.value)} placeholder="e.g. Out of hours" style={{fontSize:'0.8125rem'}} />
                    </div>
                    <div style={{display:'flex',alignItems:'flex-end',gap:'0.375rem'}}>
                      <button className="btn btn-ghost btn-sm" onClick={() => saveContact(c)} disabled={contactSaving === c.id}>{contactSaving === c.id ? '...' : 'Save'}</button>
                      <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={() => deleteContact(c.id)}>Del</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{marginTop:'1rem'}}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export { TenantDirectoryScreen };

function DistributionListScreen({ user }) {
  const [recipients, setRecipients] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editRecipient, setEditRecipient] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [copied, setCopied] = useState(null);

  async function load() {
    try {
      const [sitesRes, distRes] = await Promise.all([api.sites.list(), api.distribution.list()]);
      setSites(sitesRes.data || []);
      setRecipients(distRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function deleteRecipient(id) {
    try { await api.distribution.delete(id); setDeleteConfirmId(null); load(); }
    catch (e) { alert(e.message); }
  }

  let filtered = recipients;
  if (siteFilter) filtered = filtered.filter(r => r.site_id === siteFilter);
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(r =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.email || '').toLowerCase().includes(q) ||
      (r.unit_ref || '').toLowerCase().includes(q)
    );
  }

  const siteMap = {};
  sites.forEach(s => { siteMap[s.id] = s.name; });

  function copyEmails() {
    const emails = filtered.filter(r => r.active !== false).map(r => r.email).filter(Boolean);
    if (emails.length === 0) return;
    navigator.clipboard.writeText(emails.join(', '));
    setCopied(emails.length);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Distribution List</div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowCreate(true)}>
          <PlusIcon style={{width:'0.875rem',height:'0.875rem'}} /> Add Recipient
        </button>
      </div>
      <div className="page-content">
        <div style={{display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'1rem',flexWrap:'wrap'}}>
          <input className="input" style={{width:'220px'}} placeholder="Search name, email, unit..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="input" style={{width:'180px'}} value={siteFilter} onChange={e => setSiteFilter(e.target.value)}>
            <option value="">All Sites</option>
            {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button className="btn btn-secondary btn-sm" onClick={copyEmails} disabled={filtered.filter(r => r.active !== false).length === 0} title="Paste into the BCC field so recipients can't see each other's addresses">
            {copied ? `Copied ${copied} emails — paste into BCC` : 'Copy for BCC'}
          </button>
          <span style={{fontSize:'0.8125rem',color:'var(--text-3)'}}>{filtered.length} recipient{filtered.length!==1?'s':''}</span>
        </div>
        {loading ? (
          <div style={{display:'flex',justifyContent:'center',padding:'3rem'}}><div className="spinner" /></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><p>No distribution recipients found</p></div>
        ) : (
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Email</th><th>Unit</th><th>Site</th><th>Active</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} style={{opacity: r.active === false ? 0.45 : 1}}>
                  <td style={{fontWeight:500}}>{r.name || '—'}</td>
                  <td style={{fontSize:'0.8125rem'}}><a href={`mailto:${r.email}`} style={{color:'var(--blue)',textDecoration:'none'}}>{r.email}</a></td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{r.unit_ref || '—'}</td>
                  <td style={{color:'var(--text-2)',fontSize:'0.8125rem'}}>{siteMap[r.site_id] || '—'}</td>
                  <td><span className={`badge ${r.active !== false ? 'badge-success' : 'badge-neutral'}`}>{r.active !== false ? 'Yes' : 'No'}</span></td>
                  <td style={{textAlign:'right',display:'flex',gap:'0.375rem',justifyContent:'flex-end'}}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setEditRecipient(r)}>Edit</button>
                    {deleteConfirmId === r.id ? (
                      <>
                        <button className="btn btn-sm" style={{background:'var(--danger)',color:'#fff',border:'none'}} onClick={() => deleteRecipient(r.id)}>Yes</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setDeleteConfirmId(null)}>No</button>
                      </>
                    ) : (
                      <button className="btn btn-ghost btn-sm" style={{color:'var(--danger)'}} onClick={() => setDeleteConfirmId(r.id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate && <DistributionFormModal sites={sites} onClose={() => setShowCreate(false)} onSaved={() => { setShowCreate(false); load(); }} />}
      {editRecipient && <DistributionFormModal sites={sites} recipient={editRecipient} onClose={() => setEditRecipient(null)} onSaved={() => { setEditRecipient(null); load(); }} />}
    </div>
  );
}

function DistributionFormModal({ sites, recipient, onClose, onSaved }) {
  const isEdit = !!recipient;
  const [form, setForm] = useState({
    site_id: recipient?.site_id || '', name: recipient?.name || '',
    email: recipient?.email || '', unit_ref: recipient?.unit_ref || '',
    active: recipient ? recipient.active !== false : true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  async function save() {
    if (!isEdit && !form.site_id) { setError('Please select a site'); return; }
    if (!form.email.trim()) { setError('Email is required'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.distribution.update(recipient.id, {
          name: form.name.trim() || null,
          email: form.email.trim(),
          unit_ref: form.unit_ref.trim() || null,
          active: form.active,
        });
      } else {
        await api.distribution.create({
          site_id: form.site_id,
          name: form.name.trim() || null,
          email: form.email.trim(),
          unit_ref: form.unit_ref.trim() || null,
          active: form.active,
        });
      }
      onSaved();
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Recipient' : 'Add Recipient'}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        {error && <div className="alert alert-danger" style={{marginBottom:'1rem'}}>{error}</div>}
        {!isEdit && (
          <div className="field">
            <label className="label">Site</label>
            <select className="input" value={form.site_id} onChange={e => setForm(f=>({...f,site_id:e.target.value}))}>
              <option value="">Select site...</option>
              {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
          <div className="field">
            <label className="label">Name</label>
            <input className="input" value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="Recipient name" />
          </div>
          <div className="field">
            <label className="label">Unit Ref</label>
            <input className="input" value={form.unit_ref} onChange={e => setForm(f=>({...f,unit_ref:e.target.value}))} placeholder="e.g. Unit 12" />
          </div>
        </div>
        <div className="field">
          <label className="label">Email</label>
          <input type="email" className="input" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} placeholder="email@example.com" />
        </div>
        <div className="field">
          <label className="label" style={{display:'flex',alignItems:'center',gap:'0.5rem',cursor:'pointer'}}>
            <input type="checkbox" checked={form.active} onChange={e => setForm(f=>({...f,active:e.target.checked}))} />
            Active (included in distributions)
          </label>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Recipient'}</button>
        </div>
      </div>
    </div>
  );
}

export { DistributionListScreen };
